// =============================================================================
// UnifiedPitchAllocator.jsx - REFACTORED Main Allocator Component 
// =============================================================================
// Orchestrates all allocation functionality using extracted components
// Uses new allocationService for data management
// =============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc, setDoc, deleteDoc, collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import logo from '../../assets/images/logo2.PNG';
import { AdminOnly, useUserRole } from '../../contexts/UserContext';
import { useSubscriptionFeatures } from '../../hooks/useSubscriptionFeatures';

// Extracted components
import AllocationGrid from './AllocationGrid';
import AllocationForm from './AllocationForm';
import AllocationSidebar from './AllocationSidebar';
import PitchRenderer from '../PitchRenderer';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// Allocation services
import allocationService from '../../services/allocations/allocationService';
import allocationImportExportService from '../../services/allocations/allocationImportExportService';

// Changelog utility
import { logAllocationChange } from '../../utils/changelogUtils';

// Console capture for feedback diagnostics
import { consoleCapture } from '../../utils/consoleCapture';

// Share links hook
import { useShareLinks } from '../../hooks/useShareLinks';

// Enhanced Share Dialog with new URL format
import EnhancedShareDialog from '../EnhancedShareDialog';

// Behavior analytics for navigation tracking
import { useEventTracker } from '../../utils/useEventTracker';

// NEW: Shared facility helpers for conflict detection and day access validation
import { 
checkSharedFacilityConflicts, 
validateDayAccess, 
getConflictMessage,
loadPartnerAllocations
} from '../../utils/sharedFacilityHelpers';

// QR Code generation for share links
import { QRCodeCanvas } from 'qrcode.react';

// PDF generation for QR code download
import { jsPDF } from 'jspdf';

// =============================================================================
// HINTS SYSTEM - Sequential contextual hints
// =============================================================================

const HINT_SEQUENCE = [
{
key: 'hint_date_picker',
message: 'Change the date to view or create allocations for different days.',
position: 'bottom'
},
{
key: 'hint_select_pitch',
message: 'Select which pitch you want to allocate from the sidebar.',
position: 'right'
},
{
key: 'hint_session_type',
message: 'Choose the session type: Training, Match, or Event.',
position: 'right'
},
{
key: 'hint_team',
message: 'Select which team will be using this allocation.',
position: 'right'
},
{
key: 'hint_start_time',
message: 'Choose the start time for your allocation.',
position: 'right'
},
{
key: 'hint_duration',
message: 'Select the duration of your session.',
position: 'right'
},
{
key: 'hint_sections',
message: 'Select which sections of the pitch to allocate.',
position: 'left'
},
{
key: 'hint_add_button',
message: 'Click "Add Allocation" to save your booking.',
position: 'top'
},
{
key: 'hint_recurring',
message: 'Enable recurring allocations to automatically schedule the same session for multiple weeks.',
position: 'bottom'
},
{
key: 'hint_grid_navigation',
message: 'Click on a section with an active allocation to remove it.',
position: 'top'
}
];

const getNextHintToShow = () => {
for (const hint of HINT_SEQUENCE) {
if (!localStorage.getItem(hint.key)) {
return hint;
}
}
return null;
};

const resetAllHints = () => {
HINT_SEQUENCE.forEach(hint => {
localStorage.removeItem(hint.key);
});
window.dispatchEvent(new Event('hintDismissed'));
console.log(' All hints reset!');
};

const Hint = ({ hintKey, message, position = 'bottom', refreshTrigger = 0 }) => {
const [isVisible, setIsVisible] = useState(false);
const [coords, setCoords] = useState({ top: 0, left: 0 });
const hintRef = React.useRef(null);

useEffect(() => {
console.log('Hint useEffect triggered:', { hintKey, refreshTrigger });

// Reset visibility when refresh is triggered
setIsVisible(false);

// Check if this is the next hint to show in sequence
const nextHint = getNextHintToShow();
console.log('Next hint to show:', nextHint?.key || 'none');
console.log('Current hint key:', hintKey);
console.log('Keys match:', nextHint?.key === hintKey);

if (nextHint && nextHint.key === hintKey) {
console.log('This hint should show!', hintKey);
const timer = setTimeout(() => {
console.log('Setting visibility to true for:', hintKey);
setIsVisible(true);
}, 500);
return () => clearTimeout(timer);
} else {
console.log('This hint will not show:', hintKey, '- next hint is:', nextHint?.key || 'none');
}
}, [hintKey, refreshTrigger]);

const handleDismiss = () => {
console.log('Dismissing hint:', hintKey);
localStorage.setItem(hintKey, 'true');
setIsVisible(false);
window.dispatchEvent(new Event('hintDismissed'));
};

console.log('Hint render:', { hintKey, isVisible });

if (!isVisible) return null;

// Simple positioning - no transform tricks
const getPositionStyles = () => {
switch (position) {
case 'bottom':
return {
top: 'calc(100% + 16px)',
left: '50%',
marginLeft: '-190px', // Half of maxWidth (380px)
};
case 'top':
return {
bottom: 'calc(100% + 16px)',
left: '50%',
marginLeft: '-190px',
};
case 'right':
return {
left: 'calc(100% + 16px)',
top: '50%',
marginTop: '-60px', // Approximate half height
};
case 'left':
return {
right: 'calc(100% + 16px)',
top: '50%',
marginTop: '-60px',
};
default:
return {};
}
};

const arrowStyles = {
top: {
top: '100%',
left: '50%',
marginLeft: '-12px',
borderLeft: '12px solid transparent',
borderRight: '12px solid transparent',
borderTop: '12px solid #1e40af',
marginTop: '-1px'
},
bottom: {
bottom: '100%',
left: '50%',
marginLeft: '-12px',
borderLeft: '12px solid transparent',
borderRight: '12px solid transparent',
borderBottom: '12px solid #1e40af',
marginBottom: '-1px'
},
left: {
left: '100%',
top: '50%',
marginTop: '-12px',
borderTop: '12px solid transparent',
borderBottom: '12px solid transparent',
borderLeft: '12px solid #1e40af',
marginLeft: '-1px'
},
right: {
right: '100%',
top: '50%',
marginTop: '-12px',
borderTop: '12px solid transparent',
borderBottom: '12px solid transparent',
borderRight: '12px solid #1e40af',
marginRight: '-1px'
}
};

const innerArrowStyles = {
top: {
top: '100%',
left: '50%',
marginLeft: '-10px',
borderLeft: '10px solid transparent',
borderRight: '10px solid transparent',
borderTop: '10px solid #dbeafe',
marginTop: '0px'
},
bottom: {
bottom: '100%',
left: '50%',
marginLeft: '-10px',
borderLeft: '10px solid transparent',
borderRight: '10px solid transparent',
borderBottom: '10px solid #dbeafe',
marginBottom: '1px'
},
left: {
left: '100%',
top: '50%',
marginTop: '-10px',
borderTop: '10px solid transparent',
borderBottom: '10px solid transparent',
borderLeft: '10px solid #dbeafe',
marginLeft: '0px'
},
right: {
right: '100%',
top: '50%',
marginTop: '-10px',
borderTop: '10px solid transparent',
borderBottom: '10px solid transparent',
borderRight: '10px solid #dbeafe',
marginRight: '1px'
}
};

return (
<>
<style>{`
       @keyframes fadeIn {
         from { opacity: 0; }
         to { opacity: 1; }
       }
     `}</style>

<div
ref={hintRef}
style={{
position: 'absolute',
...getPositionStyles(),
backgroundColor: '#dbeafe',
color: '#1e3a8a',
padding: '16px 20px',
borderRadius: '12px',
fontSize: '14px',
lineHeight: '1.6',
minWidth: '280px',
maxWidth: '380px',
boxShadow: '0 8px 24px rgba(30, 64, 175, 0.25)',
zIndex: 10001,
animation: 'fadeIn 0.3s ease-in',
border: '2px solid #1e40af',
fontWeight: '500'
}}
>
<div
style={{
position: 'absolute',
width: 0,
height: 0,
...arrowStyles[position]
}}
/>

<div
style={{
position: 'absolute',
width: 0,
height: 0,
...innerArrowStyles[position]
}}
/>

<div style={{ marginBottom: '12px' }}>
{message}
</div>

<button
onClick={handleDismiss}
style={{
backgroundColor: '#1e40af',
border: 'none',
color: 'white',
padding: '6px 16px',
borderRadius: '6px',
fontSize: '13px',
cursor: 'pointer',
fontWeight: '600',
transition: 'background-color 0.2s'
}}
onMouseEnter={(e) => e.target.style.backgroundColor = '#1e3a8a'}
onMouseLeave={(e) => e.target.style.backgroundColor = '#1e40af'}
>
Got it!
</button>
</div>
</>
);
};

// =============================================================================
// CONSTANTS
// =============================================================================

const defaultSections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const timeSlots = (start = 8, end = 21.25) => {
const slots = [];
for (let h = start; h <= Math.floor(end); h++) {
for (let m = 0; m < 60; m += 15) {
if (h === Math.floor(end) && m > (end % 1) * 60) break;
const timeString = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
slots.push(timeString);
}
}
return slots;
};

const durationOptions = [
{ value: 15, label: '15 minutes' },
{ value: 20, label: '20 minutes' },
{ value: 25, label: '25 minutes' },
{ value: 30, label: '30 minutes' },
{ value: 35, label: '35 minutes' },
{ value: 40, label: '40 minutes' },
{ value: 45, label: '45 minutes' },
{ value: 50, label: '50 minutes' },
{ value: 55, label: '55 minutes' },
{ value: 60, label: '60 minutes' },
{ value: 65, label: '65 minutes' },
{ value: 70, label: '70 minutes' },
{ value: 75, label: '75 minutes' },
{ value: 80, label: '80 minutes' },
{ value: 85, label: '85 minutes' },
{ value: 90, label: '90 minutes' },
{ value: 105, label: '105 minutes' },
{ value: 120, label: '120 minutes' },
{ value: 135, label: '135 minutes' },
{ value: 150, label: '150 minutes' },
{ value: 180, label: '180 minutes' }
];

const feedbackTopics = [
'Bug Report',
'Feature Request',
'User Interface Issue',
'Performance Issue',
'Data/Sync Problem',
'Login/Authentication Issue',
'Settings Issue',
'Team Configuration Issue',
'Pitch Configuration Issue',
'Import/Export Issue',
'General Feedback',
'Other'
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const UnifiedPitchAllocator = () => {
// Router hooks
const { pitchId } = useParams();
const navigate = useNavigate();
const location = useLocation();

// Track page views with pitch context for navigation pattern analysis
// This helps identify if users are using sidebar navigation vs going back to Menu
useEventTracker(`Allocations - Pitch ${pitchId || '?'}`);

// Get date from URL query params
const searchParams = new URLSearchParams(location.search);
const dateFromUrl = searchParams.get('date');

// =============================================================================
// STATE - User & Club Data
// =============================================================================

const [user, setUser] = useState(null);
const [clubId, setClubId] = useState(null);  // FIX: Store actual club ID from Firestore
const [userRole, setUserRole] = useState(null);
const [clubInfo, setClubInfo] = useState(null);
const [branding, setBranding] = useState(null);
const [teams, setTeams] = useState([]);
const [pitchNames, setPitchNames] = useState({});
const [showGrassArea, setShowGrassArea] = useState({});
const [referencePoints, setReferencePoints] = useState({});

// Helper to check if trial is expired
const isTrialExpired = useMemo(() => {
  if (!clubInfo) return false;
  
  const now = new Date();
  const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
    clubInfo.trialEndsAt.toDate() : 
    new Date(clubInfo.trialEndsAt);
  
  const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
  const msRemaining = trialEndsAt - now;
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
  
  return subscriptionStatus === 'trial' && daysRemaining <= 0;
}, [clubInfo]);

// Get subscription features AFTER clubInfo is defined
const { canCreateRecurringAllocations, canCreateShareLinks, canImportAllocations, tierName } = useSubscriptionFeatures(clubInfo?.subscriptionTier || 'basic');

// =============================================================================
// STATE - Pitch Configuration
// =============================================================================

const [pitchSectionConfig, setPitchSectionConfig] = useState(null);
const [currentSections, setCurrentSections] = useState(defaultSections);
const [satelliteConfig, setSatelliteConfig] = useState(null);

// =============================================================================
// STATE - Multi-Facility Support
// =============================================================================

const [facilities, setFacilities] = useState([]);
const [selectedFacilityId, setSelectedFacilityId] = useState(null);

// =============================================================================
// STATE - Allocations
// =============================================================================

const [allocations, setAllocations] = useState({});
const [allPitchAllocations, setAllPitchAllocations] = useState({});
const [loading, setLoading] = useState(true);
const [savingAllocation, setSavingAllocation] = useState(false);
const [deletingAllocation, setDeletingAllocation] = useState(false);
const [deletingKeys, setDeletingKeys] = useState(new Set());
const [showExportModal, setShowExportModal] = useState(false);

// =============================================================================
// STATE - Form Data
// =============================================================================

const [date, setDate] = useState(() => {
if (dateFromUrl) return dateFromUrl;
const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, '0');
const day = String(today.getDate()).padStart(2, '0');
return `${year}-${month}-${day}`;
});
const [allocationType, setAllocationType] = useState('training');
const [team, setTeam] = useState('');
const [selectedSections, setSelectedSections] = useState([]);
const [sectionGroup, setSectionGroup] = useState('A');
const [slot, setSlot] = useState(''); // Initialize to empty, will be set by useEffect
const [duration, setDuration] = useState(30);

// =============================================================================
// STATE - Recurring Allocations
// =============================================================================

const [isRecurring, setIsRecurring] = useState(false);
const [recurringEndDate, setRecurringEndDate] = useState('');
const [recurringDates, setRecurringDates] = useState([]);
const [recurringConflicts, setRecurringConflicts] = useState({});

// =============================================================================
// STATE - UI
// =============================================================================

const [isMobile, setIsMobile] = useState(false);
const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
const [filterMenuOpen, setFilterMenuOpen] = useState(false);
const [teamFilterMenuOpen, setTeamFilterMenuOpen] = useState(false);
const [expandedSlots, setExpandedSlots] = useState({});
const [filterType, setFilterType] = useState('all');
const [selectedTeams, setSelectedTeams] = useState([]);
const [showBusinessHours, setShowBusinessHours] = useState(false);
const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'am', 'pm'
const [slotInterval, setSlotInterval] = useState('60'); // '60' = hourly, '30' = half-hourly, '15' = quarter-hourly
const [successMessage, setSuccessMessage] = useState('');
const [attemptedSubmit, setAttemptedSubmit] = useState(false);
const [hintRefresh, setHintRefresh] = useState(0);

// Delete confirmation modal state
const [deleteConfirmModal, setDeleteConfirmModal] = useState({
  isOpen: false,
  allocationKey: null,
  allocationInfo: null
});

// Window width for responsive design
const [windowWidth, setWindowWidth] = useState(
  typeof window !== 'undefined' ? window.innerWidth : 1024
);

// Track window resizing for responsive layout
useEffect(() => {
  const handleResize = () => setWindowWidth(window.innerWidth);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

// Responsive breakpoints
const isTablet = windowWidth <= 1024;
const isSmallScreen = windowWidth <= 768;

// Listen for hint dismissals to show next hint
useEffect(() => {
const handleHintDismissed = () => setHintRefresh(prev => prev + 1);
window.addEventListener('hintDismissed', handleHintDismissed);
return () => window.removeEventListener('hintDismissed', handleHintDismissed);
}, []);

// =============================================================================
// STATE - Share & Feedback
// =============================================================================

// Use share links hook with subscription validation
const {
shareLink,
showShareDialog,
showPaywall: showSharePaywall,
handleShare,
closeShareDialog,
closePaywall: closeSharePaywall
} = useShareLinks(clubInfo, canCreateShareLinks);

// Share dialog tab and copy state
const [shareTab, setShareTab] = useState('link');
const [linkCopied, setLinkCopied] = useState(false);

const [showFeedbackModal, setShowFeedbackModal] = useState(false);
const [showPremiumModal, setShowPremiumModal] = useState(false);
const [showBlockedActionModal, setShowBlockedActionModal] = useState(false);
const [premiumFeatureAttempted, setPremiumFeatureAttempted] = useState('');

// Validation Modal State
const [validationModal, setValidationModal] = useState({
  isOpen: false,
  title: '',
  message: '',
  type: 'error' // 'error', 'warning', 'info', 'success'
});

// Helper to show validation modal
const showValidationModal = (title, message, type = 'error') => {
  setValidationModal({ isOpen: true, title, message, type });
};

const closeValidationModal = () => {
  setValidationModal({ isOpen: false, title: '', message: '', type: 'error' });
};

const [feedbackForm, setFeedbackForm] = useState({
from: '',
topic: '',
content: '',
url: ''
});
const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

// =============================================================================
// PITCH NAMING HELPERS - Standardized across all components
// =============================================================================

// Normalize pitch ID to consistent format: pitch-{number}
const normalizePitchId = useCallback((pitchIdInput) => {
if (!pitchIdInput) return '';
const id = String(pitchIdInput);
// If it already has the pitch- format, return as is
if (id.startsWith('pitch-')) return id;
// If it's just "pitch1" format, convert to "pitch-1"
if (id.startsWith('pitch')) return id.replace('pitch', 'pitch-');
// If it's just a number, add pitch- prefix
return `pitch-${id}`;
}, []);

const normalizedPitchId = useMemo(() => {
return normalizePitchId(pitchId);
}, [pitchId, normalizePitchId]);

const getPitchDisplayName = useCallback((pitchNumber, pitchNamesData) => {
const normalized = normalizePitchId(pitchNumber);

// Extract just the number from "pitch-1" format for display
const extractedNumber = pitchNumber.toString().match(/(\d+)/)?.[1] || pitchNumber;

const possibleKeys = [
normalized,
`pitch${extractedNumber}`,
`pitch-${extractedNumber}`,
`Pitch ${extractedNumber}`,
`Pitch-${extractedNumber}`,
extractedNumber.toString(),
];

for (const key of possibleKeys) {
if (pitchNamesData && pitchNamesData[key]) {
const customName = pitchNamesData[key];
// Only use custom name if it's not a timestamp-based auto-generated name
// Filter out names containing 10+ digit numbers (timestamps)
if (customName && 
    customName.trim() !== '' && 
    !customName.match(/^Pitch\s*\d+$/i) &&
    !customName.match(/\d{10,}/)) {
  return customName;
}
}
}

return `Pitch ${extractedNumber}`;
}, [normalizePitchId]);

// =============================================================================
// COMPUTED VALUES
// =============================================================================

const slots = useMemo(() => timeSlots(), []);

const isAdmin = userRole === 'admin';
const canEdit = !isMobile && isAdmin;

const isProcessingRecurring = savingAllocation && isRecurring && recurringDates.length > 1;

// Filter allocations by type and teams
const filteredAllocations = useMemo(() => {
let filtered = allocations;

// Filter by allocation type
if (filterType !== 'all') {
filtered = Object.entries(filtered).reduce((acc, [key, allocation]) => {
if (allocation.allocationType === filterType || allocation.type === filterType) {
acc[key] = allocation;
}
return acc;
}, {});
}

// Filter by selected teams
if (selectedTeams.length > 0) {
filtered = Object.entries(filtered).reduce((acc, [key, allocation]) => {
const teamId = allocation.teamId || allocation.teamName;
if (selectedTeams.includes(teamId)) {
acc[key] = allocation;
}
return acc;
}, {});
}

return filtered;
}, [allocations, filterType, selectedTeams]);

// Count total allocations
const totalAllocations = useMemo(() => {
return Object.keys(filteredAllocations).length;
}, [filteredAllocations]);

// Get current pitch name using standardized helper
const currentPitchName = useMemo(() => {
return getPitchDisplayName(pitchId, pitchNames);
}, [pitchId, pitchNames, getPitchDisplayName]);

// Get slots to display (filtered by business hours if enabled)
const getSlotsToDisplay = useCallback(() => {
let filteredSlots = slots;

// Apply time filter (AM/PM)
if (timeFilter === 'am') {
filteredSlots = slots.filter(slot => {
const hour = parseInt(slot.split(':')[0]);
const minutes = parseInt(slot.split(':')[1]);
return hour < 17 || (hour === 16 && minutes <= 45);
});
} else if (timeFilter === 'pm') {
filteredSlots = slots.filter(slot => {
const hour = parseInt(slot.split(':')[0]);
return hour >= 17;
});
}

// Apply business hours filter if not a weekday
if (showBusinessHours || !isWeekday(date)) {
return filteredSlots;
}

return filteredSlots.filter(slot => {
const hour = parseInt(slot.split(':')[0]);
return hour < 8 || hour >= 17;
});
}, [slots, showBusinessHours, date, timeFilter]);

// Group slots by interval - returns anchor slots to display + a map of anchor -> all sub-slots
const getGroupedSlots = useCallback(() => {
  const allSlots = getSlotsToDisplay();
  const intervalMinutes = parseInt(slotInterval, 10); // 60, 30, or 15

  if (intervalMinutes === 15) {
    // Quarter-hourly: show every slot individually (default behaviour)
    return { displaySlots: allSlots, slotWindowMap: null };
  }

  // Build a map: anchorSlot -> [anchorSlot, +15min, +30min, ...]
  const slotWindowMap = {};
  const displaySlots = [];
  const seen = new Set();

  allSlots.forEach(slot => {
    const [h, m] = slot.split(':').map(Number);
    const totalMinutes = h * 60 + m;

    // Find which window this slot belongs to
    const windowStart = Math.floor(totalMinutes / intervalMinutes) * intervalMinutes;
    const anchorH = Math.floor(windowStart / 60);
    const anchorM = windowStart % 60;
    const anchorSlot = `${anchorH.toString().padStart(2, '0')}:${anchorM.toString().padStart(2, '0')}`;

    if (!seen.has(anchorSlot)) {
      seen.add(anchorSlot);
      displaySlots.push(anchorSlot);
      slotWindowMap[anchorSlot] = [];
    }
    slotWindowMap[anchorSlot].push(slot);
  });

  return { displaySlots, slotWindowMap };
}, [getSlotsToDisplay, slotInterval]);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const clearMessages = useCallback(() => {
setSuccessMessage('');
setAttemptedSubmit(false);
}, []);

const isLightColor = (color) => {
if (!color) return true;
const hex = color.replace('#', '');
const r = parseInt(hex.substr(0, 2), 16);
const g = parseInt(hex.substr(2, 2), 16);
const b = parseInt(hex.substr(4, 2), 16);
const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
return brightness > 155;
};

const isWeekday = (dateString) => {
const day = new Date(dateString).getDay();
return day >= 1 && day <= 5;
};

const getSectionsForPitch = useCallback((config) => {
if (!config) return defaultSections;

const configId = typeof config === 'string' ? config : config.id;

switch (configId) {
case '8-sections':
return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
case '4-sections':
return ['A', 'B', 'C', 'D'];
case '2-sections':
return ['A', 'C'];
case '1-section':
return ['A'];
default:
if (config && config.sectionsCount) {
if (config.sectionsCount === 8) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
if (config.sectionsCount === 4) return ['A', 'B', 'C', 'D'];
if (config.sectionsCount === 2) return ['A', 'C'];
if (config.sectionsCount === 1) return ['A'];
}
return defaultSections;
}
}, []);

const getMatchDayDuration = useCallback((teamIdentifier) => {
const team = teams.find(t => t.id === teamIdentifier || t.name === teamIdentifier);

console.log('getMatchDayDuration called for team:', teamIdentifier);
console.log('Found team:', team);
console.log('Team matchDuration field:', team?.matchDuration);

// Prioritize matchDuration field (playing time in minutes)
if (team && team.matchDuration) {
console.log(' Using matchDuration field:', team.matchDuration, 'minutes');
return team.matchDuration;
}

// Fallback to legacy duration field (string format like "50 minutes")
if (team && team.duration) {
const durationMatch = team.duration.match(/(\d+)/);
console.log('Duration regex match:', durationMatch);
if (durationMatch) {
const parsedDuration = parseInt(durationMatch[1]);
console.log('Parsed duration from string:', parsedDuration, 'minutes');
return parsedDuration;
}
}

console.log('No duration found, defaulting to 60 minutes');
return 60;
}, [teams]);

// Check if team can make match allocations on this pitch based on sections needed vs available
const canTeamMakeMatchAllocation = useCallback((teamIdentifier) => {
const team = teams.find(t => t.id === teamIdentifier || t.name === teamIdentifier);
if (!team || !team.matchArea) {
console.log('No team or matchArea found');
return false;
}

// Map match area to required sections count
const matchAreaToSectionsNeeded = {
'Under 6 & 7 size': 1,
'Quarter pitch': 2,
'Half Pitch': 4,
'Full Pitch': 8
};

const sectionsNeeded = matchAreaToSectionsNeeded[team.matchArea] || 1;
const sectionsAvailable = currentSections.length;

const canMakeMatch = sectionsNeeded <= sectionsAvailable;

console.log('Match allocation check for:', teamIdentifier);
console.log('Match area:', team.matchArea);
console.log('Sections needed:', sectionsNeeded);
console.log('Sections available on pitch:', sectionsAvailable);
console.log('Can make match allocation:', canMakeMatch);

return canMakeMatch;
}, [teams, currentSections]);

const getSectionOptions = useCallback((teamIdentifier) => {
const team = teams.find(t => t.id === teamIdentifier || t.name === teamIdentifier);

console.log('getSectionOptions called for team:', teamIdentifier);
console.log('Found team:', team);
console.log('Team matchArea:', team?.matchArea);
console.log('Current pitch sections available:', currentSections);

if (!team || !team.matchArea) {
console.log('No team or matchArea, returning default');
return [{ value: 'A', label: 'Default Layout (Section A)' }];
}

// Get the number of sections available on the current pitch
// More sections = smaller divisions (8 sections = eighths, 1 section = full pitch)
const pitchSectionCount = currentSections.length;

// Map matchArea to how many eighths of a pitch the team needs
// U6&7 needs 1 eighth, Quarter needs 2 eighths, Half needs 4 eighths, Full needs 8 eighths
const matchAreaToEighthsNeeded = {
'SMALL': 1,              // Needs 1/8 of pitch
'Under 6 & 7 size': 1,   // Needs 1/8 of pitch
'eighth': 1,             // Needs 1/8 of pitch
'Eighth': 1,             // Needs 1/8 of pitch
'EIGHTH': 1,             // Needs 1/8 of pitch
'QUARTER': 2,            // Needs 2/8 (1/4) of pitch
'Quarter pitch': 2,      // Needs 2/8 (1/4) of pitch
'HALF': 4,               // Needs 4/8 (1/2) of pitch
'Half Pitch': 4,         // Needs 4/8 (1/2) of pitch
'FULL': 8,               // Needs 8/8 (full) pitch
'Full Pitch': 8          // Needs 8/8 (full) pitch
};

const eighthsNeeded = matchAreaToEighthsNeeded[team.matchArea] || 8;

console.log(`Team needs ${eighthsNeeded} eighths of a pitch, pitch has ${pitchSectionCount} sections available`);

// Rule: Team can only play if pitch has AT LEAST as many sections as they need
// e.g., Full Pitch team (needs 8 eighths) can only play on 8-section pitch
// Quarter Pitch team (needs 2 eighths) can play on 8, 4, or 2 section pitches
if (eighthsNeeded > pitchSectionCount) {
console.log(` CANNOT PLAY - Team needs ${eighthsNeeded} eighths but pitch only has ${pitchSectionCount} sections`);
return [];
}

console.log(` CAN PLAY - Team needs ${eighthsNeeded} eighths, pitch has ${pitchSectionCount} sections`);

const matchAreaToSections = {
// Small/Under 6 & 7 - Single section (any one section A-H)
'SMALL': [
{ value: 'A', label: 'Section A' },
{ value: 'B', label: 'Section B' },
{ value: 'C', label: 'Section C' },
{ value: 'D', label: 'Section D' },
{ value: 'E', label: 'Section E' },
{ value: 'F', label: 'Section F' },
{ value: 'G', label: 'Section G' },
{ value: 'H', label: 'Section H' }
],
'Under 6 & 7 size': [
{ value: 'A', label: 'Section A' },
{ value: 'B', label: 'Section B' },
{ value: 'C', label: 'Section C' },
{ value: 'D', label: 'Section D' },
{ value: 'E', label: 'Section E' },
{ value: 'F', label: 'Section F' },
{ value: 'G', label: 'Section G' },
{ value: 'H', label: 'Section H' }
],
'eighth': [
{ value: 'A', label: 'Section A' },
{ value: 'B', label: 'Section B' },
{ value: 'C', label: 'Section C' },
{ value: 'D', label: 'Section D' },
{ value: 'E', label: 'Section E' },
{ value: 'F', label: 'Section F' },
{ value: 'G', label: 'Section G' },
{ value: 'H', label: 'Section H' }
],
'Eighth': [
{ value: 'A', label: 'Section A' },
{ value: 'B', label: 'Section B' },
{ value: 'C', label: 'Section C' },
{ value: 'D', label: 'Section D' },
{ value: 'E', label: 'Section E' },
{ value: 'F', label: 'Section F' },
{ value: 'G', label: 'Section G' },
{ value: 'H', label: 'Section H' }
],
'EIGHTH': [
{ value: 'A', label: 'Section A' },
{ value: 'B', label: 'Section B' },
{ value: 'C', label: 'Section C' },
{ value: 'D', label: 'Section D' },
{ value: 'E', label: 'Section E' },
{ value: 'F', label: 'Section F' },
{ value: 'G', label: 'Section G' },
{ value: 'H', label: 'Section H' }
],
// Quarter pitch (2 sections side by side)
'QUARTER': [
{ value: 'A-C', label: 'Sections A & C' },
{ value: 'B-D', label: 'Sections B & D' },
{ value: 'C-E', label: 'Sections C & E' },
{ value: 'D-F', label: 'Sections D & F' },
{ value: 'E-G', label: 'Sections E & G' },
{ value: 'F-H', label: 'Sections F & H' }
],
'Quarter pitch': [
{ value: 'A-C', label: 'Sections A & C' },
{ value: 'B-D', label: 'Sections B & D' },
{ value: 'C-E', label: 'Sections C & E' },
{ value: 'D-F', label: 'Sections D & F' },
{ value: 'E-G', label: 'Sections E & G' },
{ value: 'F-H', label: 'Sections F & H' }
],
// Half pitch (4 sections)
'HALF': [
{ value: 'A-B-C-D', label: 'Sections A-B-C-D (Top Half)' },
{ value: 'C-D-E-F', label: 'Sections C-D-E-F (Middle Half)' },
{ value: 'E-F-G-H', label: 'Sections E-F-G-H (Bottom Half)' }
],
'Half Pitch': [
{ value: 'A-B-C-D', label: 'Sections A-B-C-D (Top Half)' },
{ value: 'C-D-E-F', label: 'Sections C-D-E-F (Middle Half)' },
{ value: 'E-F-G-H', label: 'Sections E-F-G-H (Bottom Half)' }
],
// Full pitch (all 8 sections)
'FULL': [
{ value: 'A-B-C-D-E-F-G-H', label: 'Full Pitch (All Sections)' }
],
'Full Pitch': [
{ value: 'A-B-C-D-E-F-G-H', label: 'Full Pitch (All Sections)' }
]
};

let options = matchAreaToSections[team.matchArea];
console.log('Base options for matchArea "' + team.matchArea + '":', options);

if (!options) {
console.log('No match found for matchArea, returning fallback');
return [{ value: 'A', label: `${team.matchArea || 'Full Pitch'}` }];
}

// Filter to only include sections that actually exist on this pitch
const availableSectionLetters = new Set(currentSections);
const filteredOptions = options.filter(option => {
const sectionsInOption = option.value.split('-');
const allSectionsExist = sectionsInOption.every(s => availableSectionLetters.has(s));
if (!allSectionsExist) {
console.log(`Option "${option.label}" filtered out - sections ${sectionsInOption.join(',')} don't all exist on this pitch`);
}
return allSectionsExist;
});

console.log(`Filtered from ${options.length} to ${filteredOptions.length} options based on available sections`);

// If no options fit, return empty array (this will prevent match allocations)
if (filteredOptions.length === 0) {
console.log(' NO VALID OPTIONS - No section combinations exist on this pitch');
return [];
}

return filteredOptions;
}, [teams, currentSections]);

// =============================================================================
// DATA LOADING FUNCTIONS
// =============================================================================

const loadSettings = useCallback(async () => {
if (!clubInfo?.clubId) return;

try {
console.log('Loading settings for clubId:', clubInfo.clubId);

// Settings are in clubs/{clubId}/settings/general
const settingsRef = doc(db, 'clubs', clubInfo.clubId, 'settings', 'general');
const settingsDoc = await getDoc(settingsRef);

if (settingsDoc.exists()) {
const data = settingsDoc.data();
console.log('Settings loaded:', {
hasTeams: !!data.teams,
teamCount: data.teams?.length,
hasPitchNames: !!data.pitchNames,
pitchNames: data.pitchNames,
hasShowGrassArea: !!data.showGrassArea,
hasReferencePoints: !!data.referencePoints,
hasPitchReferencePoints: !!data.pitchReferencePoints,
hasPitchSectionConfig: !!data.pitchSectionConfig,
hasPitchSectionConfigurations: !!data.pitchSectionConfigurations,
hasBranding: !!data.branding
});

// Load branding (logo, colors)
if (data.branding) {
  setBranding(data.branding);
} else {
  console.log('⚠️ No branding in settings data');
}

// Load teams
if (data.teams) {
// Convert teams object to array
const teamsArray = Object.entries(data.teams).map(([key, team]) => ({
...team,
id: key
}));
console.log('Setting teams:', teamsArray.length);
setTeams(teamsArray);
}

// Load pitch names
if (data.pitchNames) {
console.log('Setting pitch names:', data.pitchNames);
setPitchNames(data.pitchNames);
}

// Load grass area settings
if (data.showGrassArea) {
setShowGrassArea(data.showGrassArea);
}

// Load reference points (try both field names for backwards compatibility)
if (data.pitchReferencePoints) {
console.log('Pitch reference points found in settings:', data.pitchReferencePoints);
console.log('Reference points keys:', Object.keys(data.pitchReferencePoints));

// Normalize pitch IDs in reference points (pitch-1  pitch1)
const normalizedRefPoints = {};
Object.keys(data.pitchReferencePoints).forEach(key => {
const normalizedKey = key.replace('-', '');
normalizedRefPoints[normalizedKey] = data.pitchReferencePoints[key];
});
console.log('Normalized reference points:', normalizedRefPoints);

setReferencePoints(normalizedRefPoints);
} else if (data.referencePoints) {
console.log('Reference points found in settings (legacy field):', data.referencePoints);
console.log('Reference points keys:', Object.keys(data.referencePoints));

// Normalize pitch IDs in reference points
const normalizedRefPoints = {};
Object.keys(data.referencePoints).forEach(key => {
const normalizedKey = key.replace('-', '');
normalizedRefPoints[normalizedKey] = data.referencePoints[key];
});

setReferencePoints(normalizedRefPoints);
} else {
console.log('No reference points in settings');
}

// Load pitch section config (try both field names for backwards compatibility)
const sectionConfigData = data.pitchSectionConfigurations || data.pitchSectionConfig;

if (sectionConfigData) {
console.log('Section config data found:', Object.keys(sectionConfigData));
console.log('Looking for pitch with ID:', pitchId, '| normalized:', normalizedPitchId);

// Try different pitch ID formats (normalized format first)
const possibleKeys = [
normalizedPitchId,     // pitch-3 (standardized format)
pitchId,               // 3
`pitch-${pitchId}`,    // pitch-3 (alternative)
`pitch${pitchId}`      // pitch3 (legacy format)
];

console.log('Trying these keys:', possibleKeys);

let config = null;
let foundKey = null;

for (const key of possibleKeys) {
if (sectionConfigData[key]) {
config = sectionConfigData[key];
foundKey = key;
break;
}
}

if (config) {
console.log(' Found config using key:', foundKey);
console.log('Config data:', config);
console.log('Config sectionsCount:', config.sectionsCount);
console.log('Config layoutName:', config.layoutName);
setPitchSectionConfig(config);
const sections = getSectionsForPitch(config);
console.log('Extracted sections:', sections);
setCurrentSections(sections);
} else {
console.log(' No config found for any of:', possibleKeys);
console.log('Available configs:', Object.keys(sectionConfigData));
// CRITICAL FIX: Reset to defaults when no custom config exists for this pitch
console.log(' Resetting to default 8 sections for pitch:', normalizedPitchId);
setPitchSectionConfig(null);
setCurrentSections(defaultSections);
}
} else {
console.log('No section config data in settings');
// CRITICAL FIX: Reset to defaults when no section config data exists at all
console.log(' Resetting to default 8 sections (no config data)');
setPitchSectionConfig(null);
setCurrentSections(defaultSections);
}
} else {
console.warn('Settings document does not exist at clubs/' + clubInfo.clubId + '/settings/general');
}
} catch (error) {
console.error('Error loading settings:', error);
}
}, [clubInfo?.clubId, normalizedPitchId, getSectionsForPitch]);

const loadAllocations = useCallback(async () => {
if (!clubInfo?.clubId || !date || !normalizedPitchId) return;

try {
console.log('Loading allocations for:', { date, pitchId: normalizedPitchId, clubId: clubInfo.clubId });

// Load OWN allocations
const pitchAllocations = await allocationService.getAllocationsForPitchAndDate(
clubInfo.clubId,
normalizedPitchId,
date
);

// NEW: Load PARTNER allocations (if sharing facility)
const { partnerAllocations, partnerClubName } = await loadPartnerAllocations(
clubInfo.clubId,
normalizedPitchId,
date
);

// Convert array to object keyed by allocation ID AND enrich with team colors
const allocationsObject = {};

// Add own allocations (normal color)
pitchAllocations.forEach(allocation => {
const teamData = teams.find(t => t.id === allocation.teamId || t.name === allocation.teamName);

allocationsObject[allocation.id] = {
...allocation,
colour: teamData?.color || '#cccccc',
color: teamData?.color || '#cccccc',
team: allocation.teamName,
teamName: allocation.teamName,
isOwnAllocation: true
};
});

// NEW: Add partner allocations (GREYED OUT)
partnerAllocations.forEach(allocation => {
allocationsObject[`partner-${allocation.id}`] = {
...allocation,
colour: '#9ca3af', // Grey color for partner allocations
color: '#9ca3af',
team: `${allocation.teamName || allocation.team} (${partnerClubName})`,
teamName: `${allocation.teamName || allocation.team} (${partnerClubName})`,
isPartnerAllocation: true,
partnerClubName: partnerClubName,
isReadOnly: true // Prevent editing/deleting
};
});

console.log('Total own allocations loaded:', pitchAllocations.length);
console.log('Partner allocations loaded:', partnerAllocations.length);
setAllocations(allocationsObject);
} catch (error) {
console.error('Error loading allocations:', error);
setAllocations({});
}
}, [date, normalizedPitchId, clubInfo?.clubId, teams]);

// Helper function to get all time slots covered by an allocation
const getAllTimeSlotsCovered = (startTime, duration) => {
const slots = [];
const [hours, minutes] = startTime.split(':').map(Number);
let currentMinutes = hours * 60 + minutes;
const endMinutes = currentMinutes + duration;

while (currentMinutes < endMinutes) {
const h = Math.floor(currentMinutes / 60);
const m = currentMinutes % 60;
const timeSlot = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
slots.push(timeSlot);
currentMinutes += 15; // Move to next 15-minute slot
}

return slots;
};

// Auto-expand slots that have allocations
useEffect(() => {
if (!allocations || Object.keys(allocations).length === 0) {
console.log('No allocations, collapsing all slots');
setExpandedSlots({});
return;
}

const slotsWithAllocations = {};

// Find all time slots covered by each allocation
Object.values(allocations).forEach(allocation => {
const startTime = allocation.slot || allocation.startTime;
const duration = allocation.duration || 30; // Default to 30 minutes if not specified

if (startTime) {
// Get all time slots this allocation spans
const coveredSlots = getAllTimeSlotsCovered(startTime, duration);
coveredSlots.forEach(slot => {
slotsWithAllocations[slot] = true;
});
}
});

console.log('Auto-expanding slots with allocations:', Object.keys(slotsWithAllocations));
setExpandedSlots(slotsWithAllocations);
}, [allocations]);

const loadAllPitchAllocations = useCallback(async () => {
if (!clubInfo?.clubId || !date || !satelliteConfig) return;

try {
// Get all pitch IDs from satellite config using standardized format
const pitchIds = satelliteConfig?.pitchBoundaries?.map(pitch => {
const pitchNum = pitch.pitchNumber || pitch.id || (satelliteConfig.pitchBoundaries.indexOf(pitch) + 1);
return normalizePitchId(pitchNum);
}) || [];

// Load allocations for all pitches
let allAllocations = {};

for (const pitchId of pitchIds) {
const pitchAllocations = await allocationService.getAllocationsForPitchAndDate(
clubInfo.clubId,
pitchId,
date
);

pitchAllocations.forEach(allocation => {
// Find the team to get color
const teamData = teams.find(t => t.id === allocation.teamId || t.name === allocation.teamName);

allAllocations[allocation.id] = {
...allocation,
colour: teamData?.color || '#cccccc',
color: teamData?.color || '#cccccc',
team: allocation.teamName,
teamName: allocation.teamName
};
});
}

setAllPitchAllocations(allAllocations);
} catch (error) {
console.error('Error loading all pitch allocations:', error);
}
}, [clubInfo?.clubId, date, satelliteConfig, teams]);

// =============================================================================
// FORM HANDLERS
// =============================================================================

const handleFormChange = (field, value) => {
clearMessages();

switch (field) {
case 'team':
setTeam(value);
console.log('Team selected:', value, '| Allocation type:', allocationType);
if ((allocationType === 'match' || allocationType === 'game') && value) {
console.log('Match/game allocation - calculating duration...');
const matchDur = getMatchDayDuration(value);
const withBuffer = matchDur + 10;
const slotsNeeded = Math.ceil(withBuffer / 15);
const actualDuration = slotsNeeded * 15;
console.log('Match duration:', matchDur, '+ buffer 10 =', withBuffer, '', actualDuration, 'mins');
setDuration(actualDuration);

const options = getSectionOptions(value);
console.log('Section options:', options);
if (options.length > 0) {
console.log('Setting sectionGroup to:', options[0].value);
setSectionGroup(options[0].value);
} else {
// No valid section options - team can't play on this pitch
console.log('WARNING: No valid section options for this team on this pitch');
const teamData = teams.find(t => t.id === value || t.name === value);
if (teamData) {
  showValidationModal(
    'Team Cannot Play on This Pitch',
    `"${teamData.name}" requires ${teamData.matchArea || 'Full Pitch'} but this pitch only has ${currentSections.length} section${currentSections.length !== 1 ? 's' : ''}.\n\nThis team cannot play matches on this pitch. Please select a different pitch or team.`,
    'error'
  );
}
// Clear the allocation form
setTeam('');
setSectionGroup('');
setSelectedSections([]);
setAllocationType('training');
setDuration(60);
setIsRecurring(false);
setRecurringEndDate("");
}
}
break;
case 'allocationType':
setAllocationType(value);
console.log('Allocation type changed to:', value, '| Current team:', team);
if ((value === 'match' || value === 'game') && team) {
console.log('Switched to match/game - calculating duration...');
const matchDur = getMatchDayDuration(team);
const withBuffer = matchDur + 10;
const slotsNeeded = Math.ceil(withBuffer / 15);
const actualDuration = slotsNeeded * 15;
console.log('Match duration:', matchDur, '+ buffer 10 =', withBuffer, '', actualDuration, 'mins');
setDuration(actualDuration);

const options = getSectionOptions(team);
console.log('Section options:', options);
if (options.length > 0) {
console.log('Setting sectionGroup to:', options[0].value);
setSectionGroup(options[0].value);
} else {
// No valid section options - team can't play on this pitch
console.log('WARNING: No valid section options for this team on this pitch');
const teamData = teams.find(t => t.id === team || t.name === team);
if (teamData) {
  showValidationModal(
    'Team Cannot Play on This Pitch',
    `"${teamData.name}" requires ${teamData.matchArea || 'Full Pitch'} but this pitch only has ${currentSections.length} section${currentSections.length !== 1 ? 's' : ''}.\n\nThis team cannot play matches on this pitch. Please select a different pitch or team.`,
    'error'
  );
}
// Clear the allocation form
setTeam('');
setSectionGroup('');
setSelectedSections([]);
setAllocationType('training');
setDuration(60);
setIsRecurring(false);
setRecurringEndDate("");
}
}
break;
case 'slot':
setSlot(value);
break;
case 'duration':
// Block duration changes for match/game allocations - duration is calculated from team settings
if (allocationType === 'match' || allocationType === 'game') {
console.log('Duration change blocked - match duration is locked based on team settings');
return;
}
// CRITICAL FIX: Parse as integer to ensure it's a number, not a string
setDuration(parseInt(value, 10));
break;
case 'sectionGroup':
setSectionGroup(value);
break;
case 'isRecurring':
if (value && !canCreateRecurringAllocations) {
setPremiumFeatureAttempted('recurring');
setShowPremiumModal(true);
return;
}
setIsRecurring(value);
break;
case 'recurringEndDate':
setRecurringEndDate(value);
break;
default:
break;
}
};

const toggleSectionSelection = (section) => {
setSelectedSections(prev => {
if (prev.includes(section)) {
return prev.filter(s => s !== section);
} else {
return [...prev, section];
}
});
clearMessages();
};

const selectAllSections = () => {
const allSections = [...currentSections];
if (showGrassArea[normalizedPitchId]) {
allSections.push('grass');
}
setSelectedSections(allSections);
};

const clearAllSections = () => {
setSelectedSections([]);
};

const changeDate = (days) => {
const currentDate = new Date(date);
currentDate.setDate(currentDate.getDate() + days);
setDate(currentDate.toISOString().split('T')[0]);
};

const toggleSlotExpanded = (slot) => {
setExpandedSlots(prev => ({
...prev,
[slot]: !prev[slot]
}));
};

const setAllSlotsExpanded = (expanded) => {
const newExpandedSlots = {};
getSlotsToDisplay().forEach(slot => {
newExpandedSlots[slot] = expanded;
});
setExpandedSlots(newExpandedSlots);
};

// =============================================================================
// TEAM FILTER HANDLERS
// =============================================================================

const toggleTeamSelection = (teamName) => {
setSelectedTeams(prev => {
if (prev.includes(teamName)) {
return prev.filter(t => t !== teamName);
} else {
return [...prev, teamName];
}
});
};

const clearTeamFilters = () => {
setSelectedTeams([]);
};

const selectAllTeams = () => {
setSelectedTeams(teams.map(t => t.name));
};

// =============================================================================
// ALLOCATION CRUD OPERATIONS
// =============================================================================

const addAllocation = async () => {
// STEP 0: Validate subscription tier for recurring allocations
if (isRecurring && !canCreateRecurringAllocations) {
setPremiumFeatureAttempted('recurring');
setShowPremiumModal(true);
return;
}

if (!team) {
setAttemptedSubmit(true);
return;
}

// STEP 0.3: Validate team's match requirements against pitch capacity
if (allocationType === 'match' || allocationType === 'game') {
const teamData = teams.find(t => t.id === team || t.name === team);
if (teamData) {
  // Map match area to required sections count
  const matchAreaToSectionsNeeded = {
    'SMALL': 1,
    'Under 6 & 7 size': 1,
    'eighth': 1,
    'Eighth': 1,
    'EIGHTH': 1,
    'QUARTER': 2,
    'Quarter pitch': 2,
    'HALF': 4,
    'Half Pitch': 4,
    'FULL': 8,
    'Full Pitch': 8
  };
  
  const sectionsNeeded = matchAreaToSectionsNeeded[teamData.matchArea] || 8;
  const sectionsAvailable = currentSections.length;
  
  if (sectionsNeeded > sectionsAvailable) {
    const matchAreaDisplay = teamData.matchArea || 'Full Pitch';
    showValidationModal(
      'Cannot Allocate Match',
      `"${teamData.name}" requires ${matchAreaDisplay} (${sectionsNeeded} sections) but this pitch only has ${sectionsAvailable} section${sectionsAvailable !== 1 ? 's' : ''}.\n\nPlease either:\n• Select a pitch with more sections, or\n• Update the team's match requirements in Team Management`,
      'error'
    );
    // Clear the allocation form
    setTeam('');
    setSectionGroup('');
    setSelectedSections([]);
    setAllocationType('training');
    setDuration(60);
    setIsRecurring(false);
    setRecurringEndDate("");
    return;
  }
}
}

//  STEP 0.5: Check advance booking limit for basic tier
const subscriptionTier = clubInfo?.subscriptionTier?.toLowerCase() || 'basic';
if (subscriptionTier === 'basic') {
const today = new Date();
today.setHours(0, 0, 0, 0);

const allocationDate = new Date(date + 'T00:00:00');
allocationDate.setHours(0, 0, 0, 0);

const daysInAdvance = Math.ceil((allocationDate - today) / (1000 * 60 * 60 * 24));

if (daysInAdvance > 31) {
setPremiumFeatureAttempted('advance-booking');
setShowPremiumModal(true);
return;
}
}

// STEP 1: Validate day access for shared facilities (ALL allocation types)
const dayAccessCheck = await validateDayAccess(clubInfo.clubId, date);
if (!dayAccessCheck.canBook) {
showValidationModal(
  'Day Access Restricted',
  `${dayAccessCheck.message}\n\nPlease check your shared facility day access schedule in Settings.`,
  'warning'
);
return;
}

// FOR MATCH/GAME ALLOCATIONS: Ensure duration is calculated correctly
let finalDuration = duration;
if (allocationType === 'match' || allocationType === 'game') {
const matchDur = getMatchDayDuration(team);
const withBuffer = matchDur + 10;
const slotsNeeded = Math.ceil(withBuffer / 15);
finalDuration = slotsNeeded * 15;
console.log('MATCH/GAME: Recalculating duration:', matchDur, '+ 10 =', withBuffer, '', finalDuration, 'mins');
}

console.log('ADD ALLOCATION CALLED');
console.log('Team:', team);
console.log('Allocation Type:', allocationType);
console.log('Duration state value:', duration);
console.log('FINAL Duration to use:', finalDuration);
console.log('Slot:', slot);

// Parse section combinations for match day allocations
let sectionsToUse;
if (allocationType === 'training') {
// Filter out empty sections for training allocations
sectionsToUse = selectedSections.filter(s => s && s.trim() !== '');
console.log('TRAINING: Using sections:', sectionsToUse);
} else {
// For match day, parse the sectionGroup (e.g., "A-C" becomes ["A", "C"])
console.log('MATCH: sectionGroup value:', sectionGroup);
console.log('MATCH: sectionGroup includes dash?', sectionGroup.includes('-'));

if (sectionGroup.includes('-')) {
sectionsToUse = sectionGroup.split('-');
console.log('MATCH: Split by dash, result:', sectionsToUse);
} else {
sectionsToUse = [sectionGroup];
console.log('MATCH: Single section, result:', sectionsToUse);
}
}

// Validate that at least one section is selected
if (sectionsToUse.length === 0) {
alert('Please select at least one section');
return;
}

console.log('FINAL sectionsToUse:', sectionsToUse);
console.log('sectionsToUse.length:', sectionsToUse.length);
console.log('Creating allocation with sections:', sectionsToUse);


// STEP 2: Check for conflicts with both own and partner club allocations
const endTime = calculateEndTime(slot, finalDuration);

// STEP 2.5: Validate allocation doesn't extend beyond facility operating hours
// All facilities close at 21:30 (last slot is 21:15, which ends at 21:30)
const FACILITY_CLOSING_TIME = '21:30';

if (endTime > FACILITY_CLOSING_TIME) {
  console.log(`🚫 Allocation end time (${endTime}) exceeds facility closing time (${FACILITY_CLOSING_TIME})`);
  showValidationModal(
    'Outside Operating Hours',
    `This allocation would end at ${endTime}, but the facility closes at ${FACILITY_CLOSING_TIME}.\n\nPlease choose an earlier start time or reduce the duration.`,
    'error'
  );
  return;
}
const conflictCheck = await checkSharedFacilityConflicts(
clubInfo.clubId,
normalizedPitchId,
date,
slot,
endTime,
sectionsToUse,
null // No excludeAllocationId since this is a new allocation
);

if (conflictCheck.hasConflict) {
const conflictMsg = getConflictMessage(conflictCheck.conflictDetails, clubInfo.clubId);
showValidationModal('Scheduling Conflict', conflictMsg, 'warning');
return;
}

setSavingAllocation(true);

try {
// FIXED: Find team by ID first (UUID), then by name (backwards compatibility)
const teamData = teams.find(t => t.id === team) || teams.find(t => t.name === team);

const allocationData = {
pitchId: normalizedPitchId,
pitchName: currentPitchName,
sections: sectionsToUse,
date,
startTime: slot,
endTime: calculateEndTime(slot, finalDuration),
duration: finalDuration, // Add duration field
teamId: teamData?.id || team,
teamName: teamData?.name || team, // FIXED: Use actual team name, not ID
allocationType,
isRecurring,
recurrencePattern: isRecurring ? {
frequency: 'weekly',
daysOfWeek: [new Date(date).getDay()],
endDate: recurringEndDate
} : null,
notes: ''
};

const result = await allocationService.createAllocation(clubInfo.clubId, allocationData);

if (result.success) {
// Log the allocation change
try {
await logAllocationChange(
clubInfo.clubId,
'created',
{
...allocationData,
team: teamData?.name || team, // FIXED: Use team name for changelog
pitch: normalizedPitchId,
pitchName: currentPitchName,
slot: slot
},
allocationType
);
console.log('Changelog entry created for new allocation');
} catch (logError) {
console.error('Error logging allocation change:', logError);
// Don't fail the allocation if logging fails
}

setSuccessMessage('Allocation created successfully!');
await loadAllocations();
await loadAllPitchAllocations();

// Reset form
setTeam('');
setSelectedSections([]);
setIsRecurring(false);
setRecurringEndDate("");
} else {
  // Don't show alert for trial expired - modal already shown
  if (result.error !== 'TRIAL_EXPIRED') {
    alert(`Error: ${result.error}`);
  }
  if (result.conflicts) {
    console.log('Conflicts:', result.conflicts);
  }
}
} catch (error) {
console.error('Error adding allocation:', error);
// Don't show alert for trial expired - modal already shown
if (error.message !== 'TRIAL_EXPIRED') {
  alert('Error creating allocation');
}
} finally {
setSavingAllocation(false);
}
};

const clearAllocation = async (allocationKey) => {
// NEW: Prevent deleting partner allocations
const clickedAllocation = allocations[allocationKey];
if (clickedAllocation?.isPartnerAllocation || clickedAllocation?.isReadOnly) {
alert(`Cannot delete allocations from ${clickedAllocation.partnerClubName}. Only the owning club can delete their allocations.`);
return;
}

// Show confirmation modal instead of window.confirm
setDeleteConfirmModal({
  isOpen: true,
  allocationKey: allocationKey,
  allocationInfo: clickedAllocation
});
};

// Actual delete function called after modal confirmation
const confirmDeleteAllocation = async () => {
const allocationKey = deleteConfirmModal.allocationKey;
const clickedAllocation = deleteConfirmModal.allocationInfo;

// Close the modal
setDeleteConfirmModal({ isOpen: false, allocationKey: null, allocationInfo: null });

setDeletingKeys(prev => new Set(prev).add(allocationKey));

try {
// Get the clicked allocation to find related ones
if (!clickedAllocation) {
throw new Error('Allocation not found');
}

// CRITICAL FIX: Check for bookingId first (for fixture scheduler allocations)
// If bookingId exists, it means this allocation was created by FixtureScheduler
// and spans multiple time slots - we need to delete ALL slots with this bookingId
let relatedKeys = [];

if (clickedAllocation.bookingId) {
// Find ALL allocations with the same bookingId (across ALL time slots and sections)
console.log('Detected bookingId:', clickedAllocation.bookingId, '- deleting all related slots');
relatedKeys = Object.keys(allocations).filter(key => {
const alloc = allocations[key];
return alloc.bookingId === clickedAllocation.bookingId;
});
} else {

        // Legacy behavior: Find allocations with same date, startTime, endTime, pitch, team, and type
        // This handles allocations created by UnifiedPitchAllocator (same booking only)
        console.log(' No bookingId - using legacy matching (same startTime, endTime, and type)');
relatedKeys = Object.keys(allocations).filter(key => {
const alloc = allocations[key];
return alloc.date === clickedAllocation.date &&
alloc.startTime === clickedAllocation.startTime &&
                 alloc.endTime === clickedAllocation.endTime &&
alloc.pitch === clickedAllocation.pitch &&
                 alloc.teamName === clickedAllocation.teamName &&
                 alloc.allocationType === clickedAllocation.allocationType;
});
}

console.log(`  Deleting ${relatedKeys.length} related allocation keys:`, relatedKeys);

// Add all related keys to deleting state for UI feedback
setDeletingKeys(prev => {
const newSet = new Set(prev);
relatedKeys.forEach(key => newSet.add(key));
return newSet;
});

// Delete all related allocations
for (const key of relatedKeys) {
const allocationToDelete = allocations[key];

const result = await allocationService.deleteAllocation(
clubInfo.clubId,
key  // pitchId is extracted from the key inside the function
);

if (!result.success) {
console.error(`Failed to delete ${key}:`, result.error);
} else {
// Log the deletion
try {
await logAllocationChange(
clubInfo.clubId,
'deleted',
{
...allocationToDelete,
team: allocationToDelete.teamName,
pitch: normalizedPitchId,
pitchName: currentPitchName
},
allocationToDelete.allocationType || allocationToDelete.type
);
console.log('Changelog entry created for deleted allocation');
} catch (logError) {
console.error('Error logging allocation deletion:', logError);
// Don't fail the deletion if logging fails
}
}
}

await loadAllocations();
await loadAllPitchAllocations();

} catch (error) {
console.error('Error deleting allocation:', error);
alert('Error deleting allocation');
} finally {
// Clear all deleting keys
setDeletingKeys(new Set());
}
};

// Cancel delete and close modal
const cancelDeleteAllocation = () => {
setDeleteConfirmModal({ isOpen: false, allocationKey: null, allocationInfo: null });
};

// NEW: Update allocation function for editing fields like opponent
const updateAllocation = async (allocationKey, updates) => {
  console.log('updateAllocation called:', { allocationKey, updates });
  
  // Prevent updating partner allocations
  const allocation = allocations[allocationKey];
  if (allocation?.isPartnerAllocation || allocation?.isReadOnly) {
    console.error('Cannot update partner allocations');
    throw new Error('Cannot update allocations from partner clubs');
  }
  
  if (!allocation) {
    console.error('Allocation not found:', allocationKey);
    throw new Error('Allocation not found');
  }
  
  try {
    // Only send the specific fields being updated (not the full allocation)
    // This prevents triggering conflict checks for time/section changes
    const updatePayload = {
      ...updates,
      // Deep merge fixtureData if provided
      fixtureData: updates.fixtureData 
        ? { ...(allocation.fixtureData || {}), ...updates.fixtureData }
        : undefined,
      updatedAt: Date.now(),
      updatedBy: auth.currentUser?.email || 'unknown'
    };
    
    // Remove undefined values
    Object.keys(updatePayload).forEach(key => {
      if (updatePayload[key] === undefined) {
        delete updatePayload[key];
      }
    });
    
    // Find all related allocations (same bookingId = same booking across multiple sections/slots)
    let keysToUpdate = [allocationKey];
    
    if (allocation.bookingId) {
      // Find ALL allocations with the same bookingId
      keysToUpdate = Object.keys(allocations).filter(key => {
        const alloc = allocations[key];
        return alloc.bookingId === allocation.bookingId;
      });
      console.log(`Found ${keysToUpdate.length} allocations with bookingId ${allocation.bookingId}`);
    } else {
      // Fallback: Find allocations with same date, startTime, endTime, team, and type
      keysToUpdate = Object.keys(allocations).filter(key => {
        const alloc = allocations[key];
        return alloc.date === allocation.date &&
               alloc.startTime === allocation.startTime &&
               alloc.endTime === allocation.endTime &&
               alloc.teamName === allocation.teamName &&
               alloc.allocationType === allocation.allocationType;
      });
      console.log(`Found ${keysToUpdate.length} related allocations (legacy matching)`);
    }
    
    // Update all related allocations
    let successCount = 0;
    let errorCount = 0;
    
    for (const key of keysToUpdate) {
      const result = await allocationService.updateAllocation(
        clubInfo.clubId,
        key,
        updatePayload
      );
      
      if (result.success) {
        successCount++;
      } else {
        console.error(`Failed to update ${key}:`, result.error);
        errorCount++;
      }
    }
    
    console.log(`Updated ${successCount}/${keysToUpdate.length} allocations`);
    
    if (errorCount > 0 && successCount === 0) {
      throw new Error('Failed to update allocations');
    }
    
    // Log the change (once for the booking)
    try {
      await logAllocationChange(
        clubInfo.clubId,
        'updated',
        {
          ...allocation,
          ...updatePayload,
          team: allocation.teamName,
          pitch: normalizedPitchId,
          pitchName: currentPitchName,
          changes: updates,
          sectionsUpdated: keysToUpdate.length
        },
        allocation.allocationType || allocation.type
      );
    } catch (logError) {
      console.error('Error logging allocation update:', logError);
      // Don't fail the update if logging fails
    }
    
    // Reload allocations to reflect changes
    await loadAllocations();
    
    console.log('Allocation updated successfully');
    return { success: true };
    
  } catch (error) {
    console.error('Error updating allocation:', error);
    throw error;
  }
};

const clearAllAllocations = async () => {
if (!window.confirm('Are you sure you want to delete ALL allocations for this pitch and date?')) {
return;
}

setDeletingAllocation(true);

try {
const allocationKeys = Object.keys(allocations);

for (const key of allocationKeys) {
await allocationService.deleteAllocation(clubInfo.clubId, key);
}

await loadAllocations();
await loadAllPitchAllocations();
setSuccessMessage('All allocations cleared!');
} catch (error) {
console.error('Error clearing all allocations:', error);
alert('Error clearing allocations');
} finally {
setDeletingAllocation(false);
}
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const calculateEndTime = (startTime, durationMinutes) => {
// Ensure durationMinutes is a number, not a string
const duration = typeof durationMinutes === 'string' ? parseInt(durationMinutes, 10) : durationMinutes;
const [hours, minutes] = startTime.split(':').map(Number);
const totalMinutes = hours * 60 + minutes + duration;
const endHours = Math.floor(totalMinutes / 60);
const endMinutes = totalMinutes % 60;
return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

// =============================================================================
// SHARE & EXPORT HANDLERS
// =============================================================================

// Share handler now uses the hook - just pass the parameters
const onShareClick = () => {
  if (blockIfTrialExpired('Share')) return;
  
  // Find the current facility name for display in share link
  const currentFacility = facilities.find(f => f.id === selectedFacilityId);
  
  handleShare({
    pitchId: normalizedPitchId,
    date: date,
    facilityId: selectedFacilityId || null,
    facilityName: currentFacility?.name || null,
    start: 6,  // Default start hour - adjust if you have dynamic time range
    end: 22,   // Default end hour - adjust if you have dynamic time range
    expiryDays: 30
  });
};

// =============================================================================
// TRIAL EXPIRED BLOCKING HELPER
// =============================================================================

const checkTrialExpired = () => {
  if (!clubInfo) return false;
  
  const now = new Date();
  const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
    clubInfo.trialEndsAt.toDate() : 
    new Date(clubInfo.trialEndsAt);
  
  const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
  const msRemaining = trialEndsAt - now;
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
  
  const isInTrial = subscriptionStatus === 'trial' && daysRemaining > 0;
  const isActive = subscriptionStatus === 'active';
  
  return !(isInTrial || isActive); // Returns true if trial expired
};

const blockIfTrialExpired = (actionName) => {
  if (isTrialExpired) {
    console.log(` Trial expired - blocking ${actionName}`);
    setShowBlockedActionModal(true);
    return true;
  }
  return false;
};

const exportAllocations = async (selectedFormat) => {
  if (blockIfTrialExpired('Export')) return;
  
try {
console.log('📤 Export allocations requested, format:', selectedFormat);

// Ensure date is a string
const dateString = typeof date === 'string' ? date : date.toISOString().split('T')[0];

// Actual service signature: exportAllocations(clubId, facilityId, pitchId, date, allocations, format, pitchName, facilityName)
const result = await allocationImportExportService.exportAllocations(
clubInfo.clubId,     // clubId
normalizedPitchId,   // facilityId (e.g., "pitch-1")
normalizedPitchId,   // pitchId (e.g., "pitch-1") - yes, it's duplicated!
dateString,          // date (e.g., "2026-02-15")
allocations,         // allocations object
selectedFormat,      // format ('csv' or 'json')
currentPitchName,    // pitchName (e.g., "Pitch 1")
''                   // facilityName (optional, empty string)
);

if (result.success) {
alert(`✅ Allocations exported successfully!\n\nFile: ${result.filename}\n\nThe file has been downloaded to your computer.`);
} else {
alert(`❌ Export failed: ${result.error}`);
}
} catch (error) {
console.error('❌ Export error:', error);
alert(`❌ Export failed: ${error.message}`);
} finally {
setShowExportModal(false);
}
};

const importAllocations = () => {
if (blockIfTrialExpired('Import')) return;

// Check subscription tier - Import is for Standard and Premium only
if (!canImportAllocations) {
setPremiumFeatureAttempted('import');
setShowPremiumModal(true);
return;
}

// Create file input element
const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = '.csv,.json';
fileInput.style.display = 'none';

fileInput.onchange = async (e) => {
const file = e.target.files[0];
if (!file) return;

try {
console.log(' Import file selected:', file.name);

// Read file content
const fileContent = await file.text();
const fileType = file.name.endsWith('.json') ? 'json' : 'csv';

// Ask user if they want to replace existing allocations
const replaceExisting = window.confirm(
`Import ${file.name}?\n\n` +
`⚠️ IMPORTANT: Would you like to REPLACE existing allocations for the dates/pitches in this file?\n\n` +
`• Click OK to REPLACE existing allocations (recommended for clean import)\n` +
`• Click Cancel to SKIP conflicting allocations (keeps existing data)\n\n` +
`Note: This import will create allocations for the dates specified in the file.`
);

console.log(' Import mode:', replaceExisting ? 'Replace' : 'Skip conflicts');

// Show loading message
const loadingMessage = 'Importing allocations... Please wait.';
const originalTitle = document.title;
document.title = '⏳ Importing...';

// Perform import
let result;
if (fileType === 'csv') {
result = await allocationImportExportService.importFromCSV(
fileContent,
clubInfo.clubId,
replaceExisting
);
} else {
result = await allocationImportExportService.importFromJSON(
fileContent,
clubInfo.clubId,
replaceExisting
);
}

document.title = originalTitle;

// Show result
if (result.success) {
const message = 
`✅ Import completed!\n\n` +
`📊 Summary:\n` +
`• Imported: ${result.imported}\n` +
`• Skipped: ${result.skipped}\n` +
`• Errors: ${result.errors}\n\n` +
(result.errors > 0 ? `⚠️ Some allocations could not be imported. Check the console for details.` : `All allocations processed successfully!`);

alert(message);

// Reload allocations
await loadAllocations();
await loadAllPitchAllocations();
} else {
alert(`❌ Import failed: ${result.error}`);
}
} catch (error) {
console.error('Import error:', error);
alert(`❌ Import failed: ${error.message}`);
} finally {
// Clean up
document.body.removeChild(fileInput);
}
};

// Trigger file picker
document.body.appendChild(fileInput);
fileInput.click();
};

// =============================================================================
// FEEDBACK HANDLERS
// =============================================================================

const openFeedbackModal = () => {
setFeedbackForm({
from: user?.email || '',
topic: '',
content: '',
url: window.location.href
});
setShowFeedbackModal(true);
};

const closeFeedbackModal = () => {
setShowFeedbackModal(false);
setFeedbackForm({
from: '',
topic: '',
content: '',
url: ''
});
};

const handleFeedbackInputChange = (field, value) => {
setFeedbackForm(prev => ({ ...prev, [field]: value }));
};

const submitFeedback = async () => {
if (!feedbackForm.from || !feedbackForm.topic || !feedbackForm.content) {
alert('Please fill in all fields');
return;
}

setIsSubmittingFeedback(true);

try {
console.log('Submitting feedback:', {
email: feedbackForm.from,
club: clubInfo?.name,
topic: feedbackForm.topic,
message: feedbackForm.content
});

// =================================================================
// STEP 1: Save to Firestore with full diagnostics (silent)
// =================================================================
try {
await addDoc(collection(db, 'feedback'), {
  // User-provided fields
  from: feedbackForm.from,
  topic: feedbackForm.topic,
  content: feedbackForm.content,
  url: feedbackForm.url,
  
  // Context fields
  clubId: clubId || null,
  clubName: clubInfo?.name || null,
  userId: user?.uid || null,
  userRole: userRole || null,
  
  // Timestamp
  submittedAt: serverTimestamp(),
  
  // =============================================================
  // SILENT DIAGNOSTICS - User never sees this
  // =============================================================
  diagnostics: {
    consoleLogs: consoleCapture.getLogs(),
    logCount: consoleCapture.getLogs().length,
    capturedAt: new Date().toISOString(),
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    referrer: document.referrer || null,
    isMobile: isMobile
  }
});
console.log('✅ Feedback saved to Firestore with diagnostics');
} catch (firestoreError) {
// Don't block submission if Firestore fails - just log it
console.error('⚠️ Firestore save failed (continuing with Netlify):', firestoreError);
}

// =================================================================
// STEP 2: Also send via Netlify Forms for email notification
// =================================================================
const formData = new FormData();
formData.append('form-name', 'feedback-form');
formData.append('email', feedbackForm.from);
formData.append('club', clubInfo?.name || '');
formData.append('topic', feedbackForm.topic);
formData.append('message', feedbackForm.content);
formData.append('url', feedbackForm.url);

const response = await fetch('/', {
method: 'POST',
headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
body: new URLSearchParams(formData).toString()
});

console.log('Response status:', response.status);

if (response.ok) {
alert('✅ Thank you for your feedback! We\'ll review it shortly.');
closeFeedbackModal();
return;
}

throw new Error('Form submission failed');
} catch (error) {
console.error('Feedback submission error:', error);

// Fallback: mailto method
const subject = `Pitchallo Feedback: ${feedbackForm.topic}`;
const body = `From: ${feedbackForm.from}
Club: ${clubInfo?.name || 'Unknown'}
Topic: ${feedbackForm.topic}
URL: ${feedbackForm.url}
Message:
${feedbackForm.content}

---
Sent from Pitchallo Feedback Form`;

const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

try {
window.location.href = mailtoLink;
setTimeout(() => {
  alert('Your email client should have opened.');
}, 1000);
closeFeedbackModal();
} catch (mailtoError) {
console.error('Mailto also failed:', mailtoError);
closeFeedbackModal();
}
} finally {
setIsSubmittingFeedback(false);
}
};

// =============================================================================
// AUTH & NAVIGATION HANDLERS
// =============================================================================

const handleLogout = async () => {
try {
await signOut(auth);
navigate('/login');
} catch (error) {
console.error('Error signing out:', error);
}
};

// =============================================================================
// EFFECTS
// =============================================================================

// Mobile detection
useEffect(() => {
const checkMobile = () => {
setIsMobile(window.innerWidth < 1024);
};

checkMobile();
window.addEventListener('resize', checkMobile);

return () => window.removeEventListener('resize', checkMobile);
}, []);

// Auth state listener
useEffect(() => {
console.log('Setting up auth listener...');
const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
console.log('Auth state changed:', { hasUser: !!currentUser, uid: currentUser?.uid });
setUser(currentUser);
if (currentUser) {
try {
const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
console.log('User doc exists:', userDoc.exists());
if (userDoc.exists()) {
const userData = userDoc.data();
console.log('User data:', { role: userData.role, clubId: userData.clubId });
setUserRole(userData.role);
setClubId(userData.clubId);  // FIX: Store clubId in state
if (userData.clubId) {
const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
console.log('Club doc exists:', clubDoc.exists());

if (clubDoc.exists()) {
const clubData = clubDoc.data();
console.log('Club data loaded:', { 
hasName: !!clubData.name, 
hasSatelliteConfig: !!clubData.satelliteConfig,
hasPitches: !!clubData.pitches,
pitchesLength: clubData.pitches?.length,
subscriptionTier: clubData.subscriptionTier 
});

const clubName = clubData.name || 
clubData.Name || 
clubData.clubName || 
clubData.ClubName || 
`Club ${userData.clubId}`;

const newClubInfo = {
clubId: userData.clubId,
name: clubName,
subscriptionTier: clubData.subscriptionTier || 'basic',
subscriptionStatus: clubData.subscriptionStatus || 'active',
trialEndsAt: clubData.trialEndsAt
};
console.log('Setting clubInfo:', newClubInfo);
setClubInfo(newClubInfo);

// Load satellite config - check BOTH satelliteConfig and root pitches field
let pitchBoundaries = null;

// Try satelliteConfig.pitchBoundaries first
if (clubData.satelliteConfig?.pitchBoundaries) {
console.log(' Found satelliteConfig.pitchBoundaries');
let configBoundaries = clubData.satelliteConfig.pitchBoundaries;
console.log('   satelliteConfig.pitchBoundaries:', {
  exists: !!configBoundaries,
  isArray: Array.isArray(configBoundaries),
  type: typeof configBoundaries,
  length: Array.isArray(configBoundaries) ? configBoundaries.length : 'N/A',
  keys: configBoundaries && typeof configBoundaries === 'object' ? Object.keys(configBoundaries).slice(0, 5) : null
});

// Convert object to array if needed
if (configBoundaries && !Array.isArray(configBoundaries) && typeof configBoundaries === 'object') {
  console.log('   Converting satelliteConfig.pitchBoundaries from object to array');
  configBoundaries = Object.values(configBoundaries);
  console.log('   Converted to array with', configBoundaries.length, 'pitches');
}

if (Array.isArray(configBoundaries) && configBoundaries.length > 0) {
  pitchBoundaries = configBoundaries;
  console.log('   Using satelliteConfig.pitchBoundaries:', pitchBoundaries.length, 'pitches');
}
}

// Fallback to root pitches field if satelliteConfig was empty or missing
if (!pitchBoundaries && clubData.pitches) {
console.log(' Falling back to root pitches field');
let pitchesData = clubData.pitches;
console.log('   Root pitches:', {
  exists: !!pitchesData,
  isArray: Array.isArray(pitchesData),
  type: typeof pitchesData,
  length: Array.isArray(pitchesData) ? pitchesData.length : 'N/A',
  keys: pitchesData && typeof pitchesData === 'object' ? Object.keys(pitchesData).slice(0, 5) : null
});

// Convert object to array if needed
if (pitchesData && !Array.isArray(pitchesData) && typeof pitchesData === 'object') {
  console.log('   Converting root pitches from object to array');
  pitchesData = Object.values(pitchesData);
  console.log('   Converted to array with', pitchesData.length, 'pitches');
}

if (Array.isArray(pitchesData) && pitchesData.length > 0) {
  pitchBoundaries = pitchesData;
  console.log('   Using root pitches:', pitchBoundaries.length, 'pitches');
}
}

// Set the satellite config with whatever we found
if (pitchBoundaries && pitchBoundaries.length > 0) {
setSatelliteConfig({
  ...clubData.satelliteConfig,
  pitchBoundaries: pitchBoundaries
});
console.log(' Set satelliteConfig with', pitchBoundaries.length, 'pitches');

// Load facilities from subcollection
try {
  const facilitiesRef = collection(db, 'clubs', userData.clubId, 'facilities');
  console.log('🔍 Querying facilities from:', `clubs/${userData.clubId}/facilities`);
  const facilitiesSnap = await getDocs(facilitiesRef);
  
  console.log('🔍 Facilities query result:', {
    empty: facilitiesSnap.empty,
    size: facilitiesSnap.size,
    docIds: facilitiesSnap.docs.map(d => d.id)
  });
  
  if (!facilitiesSnap.empty) {
    const facilitiesData = facilitiesSnap.docs.map(doc => {
      const data = doc.data();
      console.log(`🏟️ Facility "${doc.id}":`, {
        name: data.name,
        isPrimary: data.isPrimary,
        pitchCount: data.pitches?.length || 0
      });
      return {
        id: doc.id,
        ...data
      };
    });
    
    // Sort: primary first, then by name
    facilitiesData.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    
    console.log('🏟️ Loaded', facilitiesData.length, 'facilities for sidebar:', facilitiesData.map(f => f.name));
    setFacilities(facilitiesData);
    
    // Set initial selected facility from URL or default to primary
    const urlParams = new URLSearchParams(window.location.search);
    const urlFacilityId = urlParams.get('facilityId');
    if (urlFacilityId && facilitiesData.find(f => f.id === urlFacilityId)) {
      setSelectedFacilityId(urlFacilityId);
    } else {
      const primaryFacility = facilitiesData.find(f => f.isPrimary) || facilitiesData[0];
      if (primaryFacility) {
        setSelectedFacilityId(primaryFacility.id);
      }
    }
  } else {
    console.log('📍 No facilities subcollection, using legacy single-facility mode');
    // Create a synthetic facility from the legacy satelliteConfig
    const legacyFacility = {
      id: 'default',
      name: clubData.name || 'Main Facility',
      isPrimary: true,
      pitches: pitchBoundaries
    };
    setFacilities([legacyFacility]);
    setSelectedFacilityId('default');
  }
} catch (facilityError) {
  console.error('Error loading facilities:', facilityError);
}
} else {
console.warn(' No pitch data found in satelliteConfig or root pitches field');
setSatelliteConfig({
  ...clubData.satelliteConfig,
  pitchBoundaries: []
});
}
} else {
const newClubInfo = {
clubId: userData.clubId,
name: `Club ${userData.clubId}`,
subscriptionTier: 'basic',
subscriptionStatus: 'active'
};
console.log('Club doc not found, setting default clubInfo:', newClubInfo);
setClubInfo(newClubInfo);
}
} else {
console.warn('User has no clubId');
}
} else {
console.warn('User document not found');
}
} catch (error) {
console.error('Error fetching user/club data:', error);
}
}
setLoading(false);
console.log('Auth setup complete, loading set to false');
});

return () => unsubscribe();
}, []);

// Load settings when club info is available
useEffect(() => {
if (clubInfo?.clubId) {
loadSettings();
}
}, [clubInfo?.clubId, loadSettings]);

// Load allocations when dependencies change
useEffect(() => {
if (clubInfo?.clubId && date && normalizedPitchId) {
loadAllocations();
}
}, [clubInfo?.clubId, date, normalizedPitchId, loadAllocations]);

// Load all pitch allocations for sidebar
useEffect(() => {
if (clubInfo?.clubId && date && satelliteConfig) {
loadAllPitchAllocations();
}
}, [clubInfo?.clubId, date, satelliteConfig, loadAllPitchAllocations]);

// Calculate recurring dates
useEffect(() => {
if (!isRecurring || !recurringEndDate) {
setRecurringDates([]);
return;
}

const dates = [];
const baseDate = new Date(date + 'T00:00:00');
const endDate = new Date(recurringEndDate + 'T00:00:00');

// Generate dates weekly until we reach or exceed the end date
let currentDate = new Date(baseDate);
while (currentDate <= endDate) {
const year = currentDate.getFullYear();
const month = String(currentDate.getMonth() + 1).padStart(2, '0');
const day = String(currentDate.getDate()).padStart(2, '0');
dates.push(`${year}-${month}-${day}`);

// Move to next week
currentDate.setDate(currentDate.getDate() + 7);
}

setRecurringDates(dates);
}, [isRecurring, recurringEndDate, date]);

// Initialize expanded slots
useEffect(() => {
const initialExpanded = {};
const slotsToShow = getSlotsToDisplay();
slotsToShow.forEach(slot => {
initialExpanded[slot] = true;
});
setExpandedSlots(initialExpanded);
}, [getSlotsToDisplay]);

// Initialize slot to first available time when date changes
useEffect(() => {
const availableSlots = getSlotsToDisplay();
if (availableSlots.length > 0 && (!slot || !availableSlots.includes(slot))) {
setSlot(availableSlots[0]);
}
}, [date, showBusinessHours, getSlotsToDisplay, slot]);

// Close dropdowns when clicking outside
useEffect(() => {
const handleClickOutside = (event) => {
// Close team filter menu if clicking outside
if (teamFilterMenuOpen && !event.target.closest('[data-team-filter-container]')) {
setTeamFilterMenuOpen(false);
}
// Close time filter menu if clicking outside
if (filterMenuOpen && !event.target.closest('[data-time-filter-container]')) {
setFilterMenuOpen(false);
}
};

document.addEventListener('mousedown', handleClickOutside);
return () => document.removeEventListener('mousedown', handleClickOutside);
}, [teamFilterMenuOpen, filterMenuOpen]);

// =============================================================================
// RENDER
// =============================================================================

if (loading) {
return (
<div style={{
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
height: '100vh',
backgroundColor: '#f3f4f6'
}}>
<div style={{ textAlign: 'center' }}>
<div style={{
width: '48px',
height: '48px',
border: '4px solid #f3f4f6',
borderTop: '4px solid #3b82f6',
borderRadius: '50%',
animation: 'spin 1s linear infinite',
margin: '0 auto 16px'
}}></div>
<p style={{ color: '#6b7280' }}>Loading allocations...</p>
</div>
</div>
);
}

return (
<div style={{ display: 'flex', height: '100vh', backgroundColor: '#f3f4f6' }}>
{/* Responsive CSS Styles */}
<style>{`
@keyframes spin {
from { transform: rotate(0deg); }
to { transform: rotate(360deg); }
}

.recurring-icon {
display: inline-block;
}

/* Ensure all elements use border-box */
* {
box-sizing: border-box;
}

/* MOBILE: Hide trial banner on mobile devices */
@media (max-width: 1023px) {
.trial-banner,
[data-trial-banner],
.subscription-banner {
display: none !important;
}
}

/* MOBILE: Fix filter buttons row */
@media (max-width: 1023px) {
.filter-buttons-row {
flex-wrap: wrap !important;
justify-content: flex-start !important;
gap: 6px !important;
}

.filter-buttons-row button {
padding: 6px 10px !important;
font-size: 12px !important;
white-space: nowrap !important;
}

.legend-filter-container {
flex-direction: column !important;
align-items: flex-start !important;
gap: 12px !important;
}
}

/* Responsive form styles */
@media (max-width: 768px) {
.allocation-form {
padding: 16px 12px !important;
max-width: 100% !important;
overflow-x: hidden !important;
}

/* Stack form sections vertically on mobile */
.allocation-form > div {
display: flex;
flex-direction: column;
gap: 12px;
}

/* Full width inputs and buttons */
.allocation-form select,
.allocation-form input,
.allocation-form button {
width: 100% !important;
max-width: 100% !important;
}
}

@media (max-width: 1024px) {
/* Prevent overflow on tablets */
.allocation-form {
max-width: 100%;
overflow-x: hidden;
}

.main-content {
max-width: 100% !important;
}
}

/* Prevent horizontal scroll */
body {
overflow-x: hidden;
}
`}</style>
{/* Mobile Header */}
{isMobile && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
height: '60px',
backgroundColor: '#243665',
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
padding: '0 16px',
zIndex: 998,
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
}}>
<button
onClick={() => setIsMobileSidebarOpen(true)}
style={{
padding: '8px',
backgroundColor: 'transparent',
border: 'none',
cursor: 'pointer',
color: 'white',
fontSize: '24px'
}}
>
☰
</button>
<img 
src={logo}
alt="Pitch Allocator"
style={{
height: '40px',
objectFit: 'contain'
}}
/>
<div style={{ width: '40px' }}></div>
</div>
)}

{/* Sidebar */}
<div style={{ position: 'relative' }}>
<AllocationSidebar
clubInfo={clubInfo}
user={user}
userRole={userRole}
isAdmin={isAdmin}
facilities={facilities}
pitches={satelliteConfig?.pitchBoundaries || []}
pitchNames={pitchNames}
currentPitchId={normalizedPitchId}
selectedFacilityId={selectedFacilityId}
allPitchAllocations={allPitchAllocations}
allocations={allocations}
onNavigateToPitch={(pitchNum, facilityId) => {
  const params = new URLSearchParams(location.search);
  params.set('date', date);
  if (facilityId) {
    params.set('facilityId', facilityId);
    setSelectedFacilityId(facilityId);
  }
  navigate(`/allocator/pitch-${pitchNum}?${params.toString()}`);
}}
onNavigateToMenu={() => navigate('/menu')}
onNavigateToClubMap={() => navigate('/club-pitch-map')}
onNavigateToReporting={() => {
  if (blockIfTrialExpired('Reporting')) return;
  navigate('/capacity-outlook');
}}
onNavigateToSettings={() => {
  if (blockIfTrialExpired('Settings')) return;
  navigate('/settings');
}}
onFeedback={openFeedbackModal}
onLogout={handleLogout}
logoSrc={logo}
branding={branding}
isMobile={isMobile}
isMobileOpen={isMobileSidebarOpen}
onMobileClose={() => setIsMobileSidebarOpen(false)}
hintRefresh={hintRefresh}
isTrialExpired={isTrialExpired}
showTrialBanner={!isMobile}  // Hide trial banner on mobile
/>

{/* Hint positioned outside sidebar, pointing at pitch selection */}
<div style={{ 
position: 'absolute',
top: '300px',
left: '250px',
pointerEvents: 'none'
}}>
<div style={{ position: 'relative', pointerEvents: 'auto' }}>
<Hint
hintKey="hint_select_pitch"
message="Select which pitch you want to allocate from the sidebar."
position="right"
refreshTrigger={hintRefresh}
/>
</div>
</div>
</div>

{/* Main Content */}
<div 
className="main-content"
style={{ 
flex: 1,
padding: isMobile 
  ? '84px 8px 24px'  // Mobile: Fixed padding, trial banner hidden
  : isTablet 
    ? (clubInfo?.subscriptionStatus === 'trial' ? '84px 16px 24px 16px' : '24px 16px')
    : (clubInfo?.subscriptionStatus === 'trial' ? '84px 24px 24px 24px' : '24px'),
maxWidth: isTablet ? '100%' : '1400px',
marginLeft: isMobile ? '0' : '240px',
marginRight: 'auto',
overflowY: 'auto',
overflowX: 'hidden',
height: '100vh',
boxSizing: 'border-box'  
}}
>
{/* Mobile Read-Only Banner */}
{isMobile && isAdmin && (
<div style={{
backgroundColor: '#fef3c7',
border: '1px solid #fbbf24',
borderRadius: '8px',
padding: '16px',
marginBottom: '20px',
textAlign: 'center'
}}>
<div style={{
fontSize: '16px',
fontWeight: '600',
color: '#d97706',
marginBottom: '8px'
}}>
📱 Mobile View Mode
</div>
<div style={{
fontSize: '14px',
color: '#92400e'
}}>
Viewing allocations in read-only mode. To create, edit, or delete allocations, please use a desktop or laptop computer.
</div>
</div>
)}

{/* Title Bar - At Top */}
<div style={{
backgroundColor: 'white',
borderRadius: '12px',
padding: '20px 24px',
marginBottom: '24px',
boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
width: '100%'
}}>
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center'
}}>
<div>
<h1 style={{
fontSize: '24px',
fontWeight: '700',
color: '#1f2937',
margin: 0,
marginBottom: '4px'
}}>
{currentPitchName}
</h1>
<div style={{
display: 'flex',
alignItems: 'center',
gap: '12px'
}}>
<span style={{
display: 'inline-block',
backgroundColor: totalAllocations > 0 ? '#dbeafe' : '#f3f4f6',
color: totalAllocations > 0 ? '#1e40af' : '#6b7280',
padding: '4px 12px',
borderRadius: '12px',
fontSize: '13px',
fontWeight: '600'
}}>
{totalAllocations} allocation{totalAllocations !== 1 ? 's' : ''}
</span>
<span style={{
fontSize: '14px',
color: '#6b7280'
}}>
for {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
weekday: 'long', 
month: 'long', 
day: 'numeric' 
})}
</span>
</div>
</div>
</div>
</div>

{/* Date Navigation */}
<div style={{
backgroundColor: 'white',
borderRadius: '12px',
padding: '20px',
marginBottom: '24px',
boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
}}>
<div style={{
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
gap: '16px',
flexWrap: 'wrap'
}}>
<div style={{
display: 'flex',
gap: '8px',
alignItems: 'center'
}}>
<button
onClick={() => changeDate(-7)}
style={{
padding: '8px 16px',
backgroundColor: '#243665',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
fontWeight: '500'
}}
>
◀ Previous Week
</button>

<button
onClick={() => changeDate(-1)}
style={{
padding: '8px 16px',
backgroundColor: '#243665',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
fontWeight: '500'
}}
>
◀ Previous Day
</button>
</div>

<div style={{
display: 'flex',
gap: '8px',
alignItems: 'center'
}}>
<button
onClick={() => {
const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, '0');
const day = String(today.getDate()).padStart(2, '0');
setDate(`${year}-${month}-${day}`);
}}
style={{
padding: '8px 16px',
backgroundColor: '#10b981',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
fontWeight: '500'
}}
>
Today
</button>

<div style={{ position: 'relative', display: 'inline-block' }}>
<input
type="date"
value={date}
onChange={(e) => setDate(e.target.value)}
style={{
padding: '8px 16px',
border: '1px solid #d1d5db',
borderRadius: '8px',
fontSize: '14px'
}}
/>
<Hint
hintKey="hint_date_picker"
message="Change the date to view or create allocations for different days."
position="bottom"
refreshTrigger={hintRefresh}
/>
</div>

<button
onClick={() => {
resetAllHints();
setHintRefresh(prev => prev + 1);
}}
title="Reset hints"
style={{
padding: '6px 10px',
backgroundColor: 'transparent',
color: '#6b7280',
border: '1px solid #e5e7eb',
borderRadius: '6px',
cursor: 'pointer',
fontSize: '12px',
opacity: 0.6,
transition: 'opacity 0.2s'
}}
onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
>
💡
</button>
</div>

<div style={{
display: 'flex',
gap: '8px',
alignItems: 'center'
}}>
<button
onClick={() => changeDate(1)}
style={{
padding: '8px 16px',
backgroundColor: '#243665',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
fontWeight: '500'
}}
>
Next Day ▶
</button>

<button
onClick={() => changeDate(7)}
style={{
padding: '8px 16px',
backgroundColor: '#243665',
color: 'white',
border: 'none',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
fontWeight: '500'
}}
>
Next Week ▶
</button>
</div>
</div>
</div>

{/* Allocation Form */}
{canEdit && (
<AllocationForm
teams={teams}
timeSlots={getSlotsToDisplay()}
durationOptions={durationOptions}
currentSections={currentSections}
formData={{
team,
slot,
allocationType,
duration,
selectedSections,
sectionGroup,
isRecurring,
recurringEndDate,
		date
}}
onFormChange={handleFormChange}
onSubmit={addAllocation}
onClearAll={clearAllAllocations}
onSelectAllSections={selectAllSections}
onClearAllSections={clearAllSections}
onToggleSection={toggleSectionSelection}
getSectionOptions={getSectionOptions}
getMatchDayDuration={getMatchDayDuration}
canTeamMakeMatchAllocation={canTeamMakeMatchAllocation}
showGrassArea={showGrassArea[normalizedPitchId]}
normalizedPitchId={normalizedPitchId}
isSaving={savingAllocation}
isDeleting={deletingAllocation}
isProcessingRecurring={isProcessingRecurring}
successMessage={successMessage}
errorMessage=""
attemptedSubmit={attemptedSubmit}
hasConflict={false}
recurringDates={recurringDates}
recurringConflicts={recurringConflicts}
hintRefresh={hintRefresh}
canCreateRecurringAllocations={canCreateRecurringAllocations}
subscriptionTierName={tierName}
isDurationLocked={allocationType === 'match' || allocationType === 'game'}
isAdmin={isAdmin}
onSave={() => setShowExportModal(true)}
onLoad={importAllocations}
hasAllocations={Object.keys(allocations).length > 0}
/>
)}

{/* Allocation Grid */}
<div style={{
backgroundColor: 'white',
borderRadius: '12px',
padding: '24px',
boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
width: '100%'
}}>
{/* Legend and Filter Row */}
<div 
className="legend-filter-container"
style={{
display: 'flex',
justifyContent: 'space-between',
gap: '8px',
alignItems: 'center',
marginBottom: '20px',
paddingBottom: '16px',
borderBottom: '1px solid #e5e7eb',
position: 'relative',
flexWrap: isMobile ? 'wrap' : 'nowrap'
}}>
{/* Legend */}
<div style={{
display: 'flex',
alignItems: 'center',
gap: '16px'
}}>
<div style={{
display: 'flex',
alignItems: 'center',
gap: '6px'
}}>
<div style={{
width: '18px',
height: '18px',
borderRadius: '4px',
backgroundColor: '#3b82f6',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
color: 'white',
fontSize: '10px',
fontWeight: '700'
}}>
T
</div>
<span style={{
fontSize: '13px',
color: '#374151',
fontWeight: '500'
}}>
Training
</span>
</div>
<div style={{
display: 'flex',
alignItems: 'center',
gap: '6px'
}}>
<div style={{
width: '18px',
height: '18px',
borderRadius: '4px',
backgroundColor: '#ef4444',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
color: 'white',
fontSize: '10px',
fontWeight: '700'
}}>
M
</div>
<span style={{
fontSize: '13px',
color: '#374151',
fontWeight: '500'
}}>
Match
</span>
</div>
<div style={{
display: 'flex',
alignItems: 'center',
gap: '6px'
}}>
<svg
width="18"
height="18"
viewBox="0 0 18 18"
fill="none"
xmlns="http://www.w3.org/2000/svg"
className="recurring-icon"
style={{ flexShrink: 0 }}
>
<path
d="M9 2.5C5.96 2.5 3.5 4.96 3.5 8H1.5L4.5 11L7.5 8H5.5C5.5 6.07 7.07 4.5 9 4.5C10.93 4.5 12.5 6.07 12.5 8C12.5 9.93 10.93 11.5 9 11.5C8.03 11.5 7.15 11.12 6.5 10.5L5.08 11.92C6.11 12.91 7.48 13.5 9 13.5C12.04 13.5 14.5 11.04 14.5 8C14.5 4.96 12.04 2.5 9 2.5Z"
fill="#8b5cf6"
/>
</svg>
<span style={{
fontSize: '13px',
color: '#374151',
fontWeight: '500'
}}>
Recurring
</span>
</div>
</div>

{/* Share, Teams, Time Filter Buttons */}
<div 
className="filter-buttons-row"
style={{
display: 'flex',
gap: isMobile ? '6px' : '8px',
alignItems: 'center',
flexWrap: isMobile ? 'wrap' : 'nowrap'
}}>
<button
onClick={onShareClick}
style={{
padding: isMobile ? '6px 10px' : '8px 16px',
backgroundColor: '#6b7280',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: isMobile ? '12px' : '14px',
fontWeight: '500'
}}
>
🔗 Share
</button>

<div style={{ position: 'relative' }} data-team-filter-container>
<button
onClick={() => setTeamFilterMenuOpen(!teamFilterMenuOpen)}
style={{
padding: isMobile ? '6px 10px' : '8px 16px',
backgroundColor: selectedTeams.length > 0 ? '#3b82f6' : 'white',
color: selectedTeams.length > 0 ? 'white' : '#374151',
border: '1px solid #d1d5db',
borderRadius: '6px',
cursor: 'pointer',
fontSize: isMobile ? '12px' : '14px',
fontWeight: '500'
}}
>
👥 {isMobile ? '' : 'Teams: '}{selectedTeams.length > 0 ? `${selectedTeams.length}` : 'All'}
</button>

{teamFilterMenuOpen && (
<div style={{
position: 'absolute',
top: '100%',
right: 0,
marginTop: '8px',
backgroundColor: 'white',
border: '1px solid #d1d5db',
borderRadius: '8px',
boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
zIndex: 1000,
minWidth: '250px',
maxHeight: '400px',
overflowY: 'auto'
}}>
<div style={{
padding: '12px',
borderBottom: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'space-between',
gap: '8px'
}}>
<button
onClick={() => {
setSelectedTeams(teams.map(t => t.id || t.name));
}}
style={{
padding: '6px 12px',
backgroundColor: '#3b82f6',
color: 'white',
border: 'none',
borderRadius: '4px',
cursor: 'pointer',
fontSize: '12px',
flex: 1
}}
>
Select All
</button>
<button
onClick={() => {
setSelectedTeams([]);
}}
style={{
padding: '6px 12px',
backgroundColor: '#ef4444',
color: 'white',
border: 'none',
borderRadius: '4px',
cursor: 'pointer',
fontSize: '12px',
flex: 1
}}
>
Clear All
</button>
</div>

<div style={{ padding: '8px' }}>
{teams.map(team => {
const teamId = team.id || team.name;
const isSelected = selectedTeams.includes(teamId);

return (
<div
key={teamId}
onClick={() => {
if (isSelected) {
setSelectedTeams(selectedTeams.filter(id => id !== teamId));
} else {
setSelectedTeams([...selectedTeams, teamId]);
}
}}
style={{
padding: '10px 12px',
cursor: 'pointer',
borderRadius: '4px',
marginBottom: '4px',
backgroundColor: isSelected ? '#dbeafe' : 'transparent',
display: 'flex',
alignItems: 'center',
gap: '8px',
transition: 'background-color 0.2s'
}}
onMouseEnter={(e) => {
if (!isSelected) e.currentTarget.style.backgroundColor = '#f3f4f6';
}}
onMouseLeave={(e) => {
if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
}}
>
<input
type="checkbox"
checked={isSelected}
onChange={() => {}}
style={{
width: '16px',
height: '16px',
cursor: 'pointer'
}}
/>
<div
style={{
width: '12px',
height: '12px',
borderRadius: '2px',
backgroundColor: team.color || '#cccccc'
}}
/>
<span style={{
fontSize: '14px',
color: '#374151',
fontWeight: isSelected ? '500' : '400'
}}>
{team.name}
</span>
</div>
);
})}
</div>
</div>
)}
</div>

<div style={{ position: 'relative' }} data-time-filter-container>
<button
onClick={() => setFilterMenuOpen(!filterMenuOpen)}
style={{
padding: isMobile ? '6px 10px' : '8px 16px',
backgroundColor: timeFilter !== 'all' ? '#3b82f6' : 'white',
color: timeFilter !== 'all' ? 'white' : '#374151',
border: '1px solid #d1d5db',
borderRadius: '6px',
cursor: 'pointer',
fontSize: isMobile ? '12px' : '14px',
fontWeight: '500'
}}
>
🕐 {isMobile ? (timeFilter === 'am' ? 'AM' : timeFilter === 'pm' ? 'PM' : 'All') : (timeFilter === 'am' ? 'Time: AM (8:00-16:45)' : timeFilter === 'pm' ? 'Time: PM (17:00-21:15)' : 'Time: All')}
</button>

{filterMenuOpen && (
<div style={{
position: 'absolute',
top: '100%',
right: 0,
marginTop: '8px',
backgroundColor: 'white',
border: '1px solid #d1d5db',
borderRadius: '8px',
boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
zIndex: 1000,
minWidth: '200px'
}}>
<div
onClick={() => {
setTimeFilter('all');
setFilterMenuOpen(false);
}}
style={{
padding: '12px 16px',
cursor: 'pointer',
borderBottom: '1px solid #e5e7eb',
backgroundColor: timeFilter === 'all' ? '#dbeafe' : 'transparent',
fontSize: '14px',
color: '#374151',
fontWeight: timeFilter === 'all' ? '600' : '400'
}}
onMouseEnter={(e) => {
if (timeFilter !== 'all') e.currentTarget.style.backgroundColor = '#f3f4f6';
}}
onMouseLeave={(e) => {
if (timeFilter !== 'all') e.currentTarget.style.backgroundColor = 'transparent';
}}
>
All Times
</div>

<div
onClick={() => {
setTimeFilter('am');
setFilterMenuOpen(false);
}}
style={{
padding: '12px 16px',
cursor: 'pointer',
borderBottom: '1px solid #e5e7eb',
backgroundColor: timeFilter === 'am' ? '#dbeafe' : 'transparent',
fontSize: '14px',
color: '#374151',
fontWeight: timeFilter === 'am' ? '600' : '400'
}}
onMouseEnter={(e) => {
if (timeFilter !== 'am') e.currentTarget.style.backgroundColor = '#f3f4f6';
}}
onMouseLeave={(e) => {
if (timeFilter !== 'am') e.currentTarget.style.backgroundColor = 'transparent';
}}
>
AM (8:00-16:45)
</div>

<div
onClick={() => {
setTimeFilter('pm');
setFilterMenuOpen(false);
}}
style={{
padding: '12px 16px',
cursor: 'pointer',
backgroundColor: timeFilter === 'pm' ? '#dbeafe' : 'transparent',
fontSize: '14px',
color: '#374151',
fontWeight: timeFilter === 'pm' ? '600' : '400'
}}
onMouseEnter={(e) => {
if (timeFilter !== 'pm') e.currentTarget.style.backgroundColor = '#f3f4f6';
}}
onMouseLeave={(e) => {
if (timeFilter !== 'pm') e.currentTarget.style.backgroundColor = 'transparent';
}}
>
PM (17:00-21:15)
</div>
</div>
)}
</div>

{/* Slot Interval Toggle */}
<div style={{
display: 'flex',
alignItems: 'center',
backgroundColor: '#f3f4f6',
borderRadius: '8px',
padding: '3px',
gap: '2px',
border: '1px solid #e5e7eb'
}}>
{[
{ value: '60', label: isMobile ? '1h' : 'Hourly' },
{ value: '30', label: isMobile ? '30m' : 'Half-hourly' },
{ value: '15', label: isMobile ? '15m' : 'Quarter-hourly' }
].map(option => (
<button
key={option.value}
onClick={() => setSlotInterval(option.value)}
style={{
padding: isMobile ? '5px 8px' : '6px 12px',
backgroundColor: slotInterval === option.value ? 'white' : 'transparent',
color: slotInterval === option.value ? '#1f2937' : '#6b7280',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: isMobile ? '11px' : '13px',
fontWeight: slotInterval === option.value ? '600' : '400',
boxShadow: slotInterval === option.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
transition: 'all 0.15s ease',
whiteSpace: 'nowrap'
}}
>
{option.label}
</button>
))}
</div>
</div>
</div>

<div style={{ position: 'relative' }}>
<AllocationGrid
date={date}
pitchId={pitchId}
normalizedPitchId={normalizedPitchId}
timeSlots={getGroupedSlots().displaySlots}
slotWindowMap={getGroupedSlots().slotWindowMap}
allocations={filteredAllocations}
expandedSlots={expandedSlots}
onToggleSlot={toggleSlotExpanded}
referencePoints={referencePoints}
showGrassArea={showGrassArea}
pitchSectionConfig={pitchSectionConfig}
deletingKeys={deletingKeys}
canEdit={canEdit}
onClearAllocation={clearAllocation}
onUpdateAllocation={updateAllocation}
isLightColor={isLightColor}
teams={teams}
/>
<Hint
hintKey="hint_grid_navigation"
message="If you want to remove a session, you can click any section and the entire allocation will be deleted."
position="top"
refreshTrigger={hintRefresh}
/>
</div>
</div>

{/* Enhanced Share Dialog with new URL format */}
{showShareDialog && (
  <EnhancedShareDialog
    clubId={clubInfo?.clubId}
    clubName={clubInfo?.name}
    clubLogo={clubInfo?.logo}
    date={date}
    facilityName={facilities.find(f => f.id === selectedFacilityId)?.name}
    onClose={closeShareDialog}
  />
)}

{/* Share Paywall Modal */}
<PremiumFeatureModal
isOpen={showSharePaywall}
onClose={closeSharePaywall}
featureName="Share Links"
currentTier={clubInfo?.subscriptionTier || 'basic'}
requiredTiers={['standard', 'premium']}
benefits={[
"Share pitch allocations with parents, coaches, and teams",
"Create secure, time-limited share links",
"Allow read-only access without requiring login",
"Perfect for keeping everyone informed of schedules",
"Automatically update when allocations change"
]}
upgradeUrl="/pricing"
/>

{/* Premium Feature Modal (Dynamic) */}
<PremiumFeatureModal
isOpen={showPremiumModal}
onClose={() => setShowPremiumModal(false)}
featureName={
premiumFeatureAttempted === 'import' 
? 'Import Allocations' 
: premiumFeatureAttempted === 'advance-booking'
? 'Advanced Booking'
: 'Recurring Allocations'
}
currentTier={clubInfo?.subscriptionTier || 'basic'}
requiredTiers={
premiumFeatureAttempted === 'advance-booking'
? ['standard', 'premium']
: premiumFeatureAttempted === 'import' 
? ['standard', 'premium'] 
: ['premium']
}
benefits={
premiumFeatureAttempted === 'advance-booking'
? [
"Book allocations up to 12 months in advance",
"Plan entire seasons ahead of time",
"Secure premium time slots early",
"Better coordination for long-term planning",
"Ideal for clubs managing multiple teams and competitions"
]
: premiumFeatureAttempted === 'import' 
? [
"Import allocations from CSV or JSON files",
"Bulk upload schedules created in spreadsheets",
"Migrate data from other scheduling systems",
"Save hours when setting up recurring schedules",
"Perfect for importing entire season schedules at once"
]
: [
"Automatically create allocations for multiple weeks at once",
"Perfect for regular training sessions that repeat weekly",
"Save hours of manual data entry each season",
"Ensure consistent scheduling for your teams",
"Reduce errors from manual allocation creation"
]
}
upgradeUrl="/pricing"
/>

{/* Trial Expired Modal */}
<TrialExpiredModal
isOpen={showBlockedActionModal}
onClose={() => setShowBlockedActionModal(false)}
/>

{/* Feedback Modal */}
{showFeedbackModal && (
<div
style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.6)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
zIndex: 10000,
padding: '20px',
overflowY: 'auto'
}}
onClick={(e) => {
if (e.target === e.currentTarget) {
closeFeedbackModal();
}
}}
>
<div
style={{
backgroundColor: 'white',
borderRadius: '12px',
maxWidth: '600px',
width: '100%',
boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
maxHeight: '90vh',
display: 'flex',
flexDirection: 'column',
margin: 'auto'
}}
onClick={(e) => e.stopPropagation()}
>
<div style={{
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
padding: '20px',
borderBottom: '1px solid #e5e7eb'
}}>
<h2 style={{
fontSize: '20px',
fontWeight: '600',
color: '#1f2937',
margin: 0
}}>
Send Feedback or Report a Bug
</h2>
<button
onClick={closeFeedbackModal}
style={{
background: 'none',
border: 'none',
fontSize: '24px',
color: '#9ca3af',
cursor: 'pointer',
padding: '4px 8px',
lineHeight: 1
}}
>
×
</button>
</div>

<div style={{
display: 'flex',
flexDirection: 'column',
gap: '16px',
padding: '20px',
overflowY: 'auto'
}}>
<div>
<label style={{
display: 'block',
fontSize: '14px',
fontWeight: '500',
color: '#374151',
marginBottom: '6px'
}}>
From (Your Email)
</label>
<input
type="email"
value={feedbackForm.from}
onChange={(e) => handleFeedbackInputChange('from', e.target.value)}
placeholder="your.email@example.com"
style={{
width: '100%',
padding: '12px',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '16px',
boxSizing: 'border-box',
minHeight: '44px'
}}
/>
</div>

<div>
<label style={{
display: 'block',
fontSize: '14px',
fontWeight: '500',
color: '#374151',
marginBottom: '6px'
}}>
Topic
</label>
<select
value={feedbackForm.topic}
onChange={(e) => handleFeedbackInputChange('topic', e.target.value)}
style={{
width: '100%',
padding: '12px',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '16px',
boxSizing: 'border-box',
backgroundColor: 'white',
minHeight: '44px'
}}
>
<option value="">Select a topic...</option>
{feedbackTopics.map((topic, index) => (
<option key={index} value={topic}>{topic}</option>
))}
</select>
</div>

<div>
<label style={{
display: 'block',
fontSize: '14px',
fontWeight: '500',
color: '#374151',
marginBottom: '6px'
}}>
Page URL
</label>
<input
type="text"
value={feedbackForm.url}
onChange={(e) => handleFeedbackInputChange('url', e.target.value)}
placeholder="The page where you encountered this issue"
style={{
width: '100%',
padding: '8px 12px',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '14px',
boxSizing: 'border-box',
backgroundColor: '#f9fafb',
fontFamily: 'monospace',
color: '#6b7280'
}}
/>
<p style={{
fontSize: '12px',
color: '#9ca3af',
marginTop: '4px',
marginBottom: '0'
}}>
Auto-filled with current page URL
</p>
</div>

<div>
<label style={{
display: 'block',
fontSize: '14px',
fontWeight: '500',
color: '#374151',
marginBottom: '6px'
}}>
Message
</label>
<textarea
value={feedbackForm.content}
onChange={(e) => handleFeedbackInputChange('content', e.target.value)}
placeholder="Please describe your feedback, bug report, or suggestion in detail..."
rows={6}
style={{
width: '100%',
padding: '12px',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '16px',
boxSizing: 'border-box',
resize: 'vertical',
fontFamily: 'inherit',
minHeight: '120px'
}}
/>
</div>
</div>

<div style={{
display: 'flex',
justifyContent: 'flex-end',
gap: '12px',
padding: '20px',
paddingTop: '16px',
borderTop: '1px solid #e5e7eb'
}}>
<button
onClick={closeFeedbackModal}
style={{
padding: '12px 20px',
backgroundColor: 'white',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '14px',
cursor: 'pointer',
color: '#374151',
minHeight: '44px'
}}
>
Cancel
</button>
<button
onClick={submitFeedback}
disabled={isSubmittingFeedback}
style={{
padding: '12px 20px',
backgroundColor: isSubmittingFeedback ? '#9ca3af' : '#22c55e',
border: 'none',
borderRadius: '6px',
fontSize: '14px',
cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
color: 'white',
fontWeight: '500',
opacity: isSubmittingFeedback ? 0.7 : 1,
minHeight: '44px'
}}
>
{isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
</button>
</div>
</div>
</div>
)}
</div>

{/* Validation Modal */}
{validationModal.isOpen && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.5)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
zIndex: 10001,
padding: '20px'
}}>
<div style={{
backgroundColor: 'white',
borderRadius: '16px',
padding: '28px',
maxWidth: '440px',
width: '100%',
textAlign: 'center',
boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
}}>
<div style={{
width: '56px',
height: '56px',
backgroundColor: validationModal.type === 'success' ? '#d1fae5' : 
                validationModal.type === 'warning' ? '#fef3c7' : '#fee2e2',
borderRadius: '50%',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
margin: '0 auto 16px'
}}>
{validationModal.type === 'success' ? (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
) : validationModal.type === 'warning' ? (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
) : (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
)}
</div>
<h3 style={{ 
fontSize: '18px', 
fontWeight: '600', 
color: '#111827', 
marginBottom: '10px',
margin: '0 0 10px 0'
}}>
{validationModal.title}
</h3>
<p style={{ 
fontSize: '14px', 
color: '#6b7280', 
marginBottom: '20px',
whiteSpace: 'pre-line',
lineHeight: '1.5',
margin: '0 0 20px 0'
}}>
{validationModal.message}
</p>
<button
onClick={closeValidationModal}
style={{
width: '100%',
padding: '12px 20px',
backgroundColor: validationModal.type === 'success' ? '#10b981' : 
                validationModal.type === 'warning' ? '#f59e0b' : '#3b82f6',
color: 'white',
border: 'none',
borderRadius: '8px',
fontSize: '15px',
fontWeight: '500',
cursor: 'pointer',
transition: 'all 0.15s ease'
}}
>
{validationModal.type === 'success' ? 'Continue' : 'OK'}
</button>
</div>
</div>
)}

{/* Delete Confirmation Modal */}
{deleteConfirmModal.isOpen && (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.5)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
zIndex: 10000
}}>
<div style={{
backgroundColor: 'white',
borderRadius: '12px',
padding: '24px',
maxWidth: '400px',
width: '90%',
boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
}}>
{/* Modal Header */}
<div style={{
display: 'flex',
alignItems: 'center',
gap: '12px',
marginBottom: '16px'
}}>
<div style={{
width: '40px',
height: '40px',
borderRadius: '50%',
backgroundColor: '#fef2f2',
display: 'flex',
alignItems: 'center',
justifyContent: 'center'
}}>
<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#dc2626" strokeWidth="2"/>
<path d="M10 6V10" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
<circle cx="10" cy="14" r="1" fill="#dc2626"/>
</svg>
</div>
<h3 style={{
margin: 0,
fontSize: '18px',
fontWeight: '600',
color: '#111827'
}}>
Remove Allocation
</h3>
</div>

{/* Modal Body */}
<p style={{
margin: '0 0 8px 0',
fontSize: '14px',
color: '#4b5563',
lineHeight: '1.5'
}}>
Are you sure you want to remove this allocation?
</p>

{/* Allocation Details */}
{deleteConfirmModal.allocationInfo && (
<div style={{
backgroundColor: '#f9fafb',
borderRadius: '8px',
padding: '12px',
marginBottom: '20px',
border: '1px solid #e5e7eb'
}}>
<div style={{ fontSize: '14px', color: '#374151' }}>
<strong>{deleteConfirmModal.allocationInfo.teamName}</strong>
</div>
<div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
{deleteConfirmModal.allocationInfo.startTime} - {deleteConfirmModal.allocationInfo.endTime} • Section {deleteConfirmModal.allocationInfo.sections?.join(', ') || deleteConfirmModal.allocationInfo.section}
</div>
<div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
{deleteConfirmModal.allocationInfo.allocationType === 'match' ? '⚽ Match' : '🏃 Training'}
</div>
</div>
)}

{/* Modal Actions */}
<div style={{
display: 'flex',
gap: '12px',
justifyContent: 'flex-end'
}}>
<button
onClick={cancelDeleteAllocation}
style={{
padding: '10px 20px',
backgroundColor: 'white',
border: '1px solid #d1d5db',
borderRadius: '6px',
fontSize: '14px',
fontWeight: '500',
color: '#374151',
cursor: 'pointer',
transition: 'all 0.15s ease'
}}
onMouseEnter={(e) => {
e.target.style.backgroundColor = '#f9fafb';
e.target.style.borderColor = '#9ca3af';
}}
onMouseLeave={(e) => {
e.target.style.backgroundColor = 'white';
e.target.style.borderColor = '#d1d5db';
}}
>
No, Keep It
</button>
<button
onClick={confirmDeleteAllocation}
style={{
padding: '10px 20px',
backgroundColor: '#dc2626',
border: 'none',
borderRadius: '6px',
fontSize: '14px',
fontWeight: '500',
color: 'white',
cursor: 'pointer',
transition: 'all 0.15s ease'
}}
onMouseEnter={(e) => {
e.target.style.backgroundColor = '#b91c1c';
}}
onMouseLeave={(e) => {
e.target.style.backgroundColor = '#dc2626';
}}
>
Yes, Remove
</button>
</div>
</div>
</div>
)}

{/* Export Format Modal */}
{showExportModal && (
<div
style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.7)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
zIndex: 10000,
backdropFilter: 'blur(4px)'
}}
onClick={() => setShowExportModal(false)}
>
<div
style={{
backgroundColor: '#1e293b',
borderRadius: '16px',
padding: '32px',
maxWidth: '400px',
width: '90%',
boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
border: '1px solid rgba(255, 255, 255, 0.1)'
}}
onClick={(e) => e.stopPropagation()}
>
<h3 style={{
margin: '0 0 8px 0',
fontSize: '24px',
fontWeight: '700',
color: 'white'
}}>
Export Allocations
</h3>
<p style={{
margin: '0 0 24px 0',
fontSize: '14px',
color: 'rgba(255, 255, 255, 0.7)'
}}>
Choose your export format
</p>

<div style={{
display: 'flex',
flexDirection: 'column',
gap: '12px'
}}>
<button
onClick={() => exportAllocations('json')}
style={{
padding: '16px 20px',
backgroundColor: 'rgba(59, 130, 246, 0.1)',
border: '2px solid rgba(59, 130, 246, 0.3)',
borderRadius: '12px',
cursor: 'pointer',
transition: 'all 0.2s ease',
textAlign: 'left'
}}
onMouseEnter={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
e.currentTarget.style.transform = 'translateY(-2px)';
}}
onMouseLeave={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
e.currentTarget.style.transform = 'translateY(0)';
}}
>
<div style={{
fontSize: '16px',
fontWeight: '600',
color: 'white',
marginBottom: '4px'
}}>
📄 JSON Format
</div>
<div style={{
fontSize: '13px',
color: 'rgba(255, 255, 255, 0.6)'
}}>
Save/Load allocations in Pitchallo
</div>
</button>

<button
onClick={() => exportAllocations('csv')}
style={{
padding: '16px 20px',
backgroundColor: 'rgba(16, 185, 129, 0.1)',
border: '2px solid rgba(16, 185, 129, 0.3)',
borderRadius: '12px',
cursor: 'pointer',
transition: 'all 0.2s ease',
textAlign: 'left'
}}
onMouseEnter={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
e.currentTarget.style.transform = 'translateY(-2px)';
}}
onMouseLeave={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
e.currentTarget.style.transform = 'translateY(0)';
}}
>
<div style={{
fontSize: '16px',
fontWeight: '600',
color: 'white',
marginBottom: '4px'
}}>
📊 CSV Format
</div>
<div style={{
fontSize: '13px',
color: 'rgba(255, 255, 255, 0.6)'
}}>
Open in Excel or Google Sheets
</div>
</button>

<button
onClick={() => setShowExportModal(false)}
style={{
marginTop: '8px',
padding: '12px',
backgroundColor: 'transparent',
border: '1px solid rgba(255, 255, 255, 0.2)',
borderRadius: '8px',
cursor: 'pointer',
fontSize: '14px',
color: 'rgba(255, 255, 255, 0.7)',
fontWeight: '500',
transition: 'all 0.2s ease'
}}
onMouseEnter={(e) => {
e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
e.currentTarget.style.color = 'white';
}}
onMouseLeave={(e) => {
e.currentTarget.style.backgroundColor = 'transparent';
e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
}}
>
Cancel
</button>
</div>
</div>
</div>
)}

</div>
);
};

// Export Hint component for use in child components
export { Hint, getNextHintToShow };

export default UnifiedPitchAllocator;
